import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Checkbox, Dropdown, Form, Icon, Menu, Message, Modal, Tab } from 'semantic-ui-react';
import { useSelector } from 'react-redux';
import { usePopup } from '../../lib/popup';

import InfoPopup from '../InfoPopup';
import DeleteStep from '../DeleteStep';
import ConfirmStep from '../ConfirmStep';
import selectors from '../../selectors';
import getViewsBase from '../../utils/get-views-base';
import {
  CONTENT_TYPE_ICON_ITEMS,
  buildContentTypeIconOption,
  getContentTypeIconTitle,
  searchContentTypeIconOptions,
} from '../../constants/ContentTypeIcons';

import styles from './PointSystemModal.module.scss';

// Константа для представления бесконечности в базе данных
const INFINITY_VALUE = 1000000000;
const DEFAULT_SHIFTS_PER_PLAN = 15;
const SHIFTS_PER_PLAN_INPUT_PATTERN = /^\d+(\.\d+)?$/;
const SHIFTS_PER_PLAN_TYPING_PATTERN = /^\d*(\.\d*)?$/;

const normalizeShiftsPerPlanInput = (value) => String(value).trim().replace(',', '.');

const isHiddenEntity = (entity) => entity && entity.isHidden === true;

const isValidShiftsPerPlanValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  const normalized = normalizeShiftsPerPlanInput(value);
  if (!SHIFTS_PER_PLAN_INPUT_PATTERN.test(normalized)) {
    return false;
  }

  return Number(normalized) > 0;
};

const resolveShiftsPerPlanValue = (value) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : DEFAULT_SHIFTS_PER_PLAN;
};

const CONTENT_TYPE_ICON_OPTIONS = CONTENT_TYPE_ICON_ITEMS.map((item) =>
  buildContentTypeIconOption(item.name, item.title, styles.contentTypeIconOption),
);

// Вспомогательные функции для работы с бесконечностью
const isInfinity = (value) => value === INFINITY_VALUE || value === INFINITY_VALUE.toString();
// Для поля "От" минус бесконечность также представляется как INFINITY_VALUE (1000000000)
const isFromInfinity = (value) => value === INFINITY_VALUE || value === INFINITY_VALUE.toString();

const hasPositiveContentViewPlanValues = (values) => {
  if (!values || typeof values !== 'object' || Array.isArray(values)) {
    return false;
  }

  return Object.keys(values).some((key) => Number(values[key]) > 0);
};

const isContentViewPlanFilled = (plan, typeKeys) => {
  if (!plan) {
    return false;
  }

  if (Array.isArray(typeKeys) && typeKeys.length > 0) {
    return typeKeys.some((key) => getViewsBase(plan, key) > 0);
  }

  let values = plan.values;
  if (typeof values === 'string') {
    try {
      values = JSON.parse(values);
    } catch (error) {
      values = null;
    }
  }

  if (hasPositiveContentViewPlanValues(values)) {
    return true;
  }

  return (
    Number(plan.photo) > 0 || Number(plan.video) > 0 || Number(plan.caseCount) > 0
  );
};

const getDecimalPlacesFromString = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  const str = String(value);
  const dotIndex = str.indexOf('.');
  if (dotIndex === -1) {
    return 0;
  }

  return Math.max(0, str.length - dotIndex - 1);
};

const formatScaledIntToInput = (scaledInt, scale, maxDecimals) => {
  if (!Number.isFinite(scaledInt) || !Number.isFinite(scale) || scale <= 0) {
    return '';
  }

  const value = scaledInt / scale;
  if (!maxDecimals) {
    return String(value);
  }

  const fixed = value.toFixed(maxDecimals);
  return fixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};

// SVG иконка бесконечности
const InfinityIcon = React.memo(() => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={styles.infinityIcon}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    strokeWidth="2"
    stroke="currentColor"
    fill="none"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path stroke="none" d="M0 0h24v24H0z" />
    <path d="M9.828 9.172a4 4 0 1 0 0 5.656 a10 10 0 0 0 2.172 -2.828a10 10 0 0 1 2.172 -2.828 a4 4 0 1 1 0 5.656a10 10 0 0 1 -2.172 -2.828a10 10 0 0 0 -2.172 -2.828" />
  </svg>
));

InfinityIcon.propTypes = {};

const SectionInfo = React.memo(({ title, content }) => {
  const InfoPopupData = usePopup(InfoPopup);

  return (
    <InfoPopupData title={title} content={content || title}>
      <button type="button" className={styles.infoButton}>
        <Icon name="info circle" />
      </button>
    </InfoPopupData>
  );
});

SectionInfo.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
};

const PointSystemModal = React.memo(
  ({
    open,
    pointSystem,
    dampingCoefficients,
    employeePlans,
    contentTypes,
    contentTypeTemplates,
    contentViewPlans,
    onEmployeePlansFetch,
    onEmployeePlanCreate,
    onEmployeePlanUpdate,
    onEmployeePlanDelete,
    onContentViewPlansFetch,
    onContentViewPlanGetOrCreate,
    onContentViewPlanUpdate,
    onContentCardDefaultsFetch,
    onContentCardDefaultCreate,
    onContentCardDefaultUpdate,
    onContentCardDefaultDelete,
    onDampingCoefficientsFetch,
    onDampingCoefficientCreate,
    onDampingCoefficientUpdate,
    onDampingCoefficientDelete,
    onContentTypesFetch,
    onContentTypeCreate,
    onContentTypeUpdate,
    onContentTypeDelete,
    onContentTypeTemplatesFetch,
    onContentTypeTemplateCreate,
    onContentTypeTemplateUpdate,
    onContentTypeTemplateDelete,
    onContentTypeTemplateItemCreate,
    onContentTypeTemplateItemUpdate,
    onContentTypeTemplateItemDelete,
    onContentTypeTemplateApply,
    onPointSystemUpdate,
    onSelectTemplate,
    onClose,
    canEditPointSystemSettings,
    selectedTemplateId: selectedTemplateIdProp,
    boardSourceTemplateId,
    currentBoardName: currentBoardNameProp,
  }) => {
    const [draftsById, setDraftsById] = useState({});
    const [nameDraft, setNameDraft] = useState('');
    const [nameError, setNameError] = useState(false);
    const [shiftsPerPlanDraft, setShiftsPerPlanDraft] = useState(String(DEFAULT_SHIFTS_PER_PLAN));
    const [shiftsPerPlanError, setShiftsPerPlanError] = useState(false);
    const [contentCardDefaultDraftsById, setContentCardDefaultDraftsById] = useState({});
    const [contentCardDefaultsServerById, setContentCardDefaultsServerById] = useState({});
    const [contentViewPlanYear, setContentViewPlanYear] = useState(() => new Date().getFullYear());
    const [contentViewPlanMonth, setContentViewPlanMonth] = useState(1);
    const contentViewPlanKey = useMemo(
      () => `${contentViewPlanYear}-${contentViewPlanMonth}`,
      [contentViewPlanYear, contentViewPlanMonth],
    );
    const [contentViewPlanDraftsByKey, setContentViewPlanDraftsByKey] = useState({});
    const [contentViewPlanServerByKey, setContentViewPlanServerByKey] = useState({});
    const [contentViewPlanDirtyByKey, setContentViewPlanDirtyByKey] = useState({});
    const [contentViewPlanForm, setContentViewPlanForm] = useState(() => ({ values: {} }));
    const [dampingDraftsById, setDampingDraftsById] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const [dampingValidationErrors, setDampingValidationErrors] = useState({});
    const [dampingConflictErrors, setDampingConflictErrors] = useState({});
    const [employeePlanValidationErrors, setEmployeePlanValidationErrors] = useState({});
    const [employeePlanConflictErrors, setEmployeePlanConflictErrors] = useState({});
    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const [saveMessage, setSaveMessage] = useState(null); // { type: 'success' | 'error', text: string }
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
    const [showDampingGapsConfirmation, setShowDampingGapsConfirmation] = useState(false);
    const [templateIdToDelete, setTemplateIdToDelete] = useState(null);
    const [templateIdToRestore, setTemplateIdToRestore] = useState(null);
    const [showApplyToBoardConfirmation, setShowApplyToBoardConfirmation] = useState(false);
    const [showHiddenTemplates, setShowHiddenTemplates] = useState(false);
    const [pendingSelectTemplateName, setPendingSelectTemplateName] = useState(null);
    const [contentTypeFormOpen, setContentTypeFormOpen] = useState(false);
    const [contentTypeFormMode, setContentTypeFormMode] = useState('create'); // 'create' | 'edit'
    const [contentTypeFormId, setContentTypeFormId] = useState(null);
    const [contentTypeFormTitle, setContentTypeFormTitle] = useState('');
    const [contentTypeFormIcon, setContentTypeFormIcon] = useState('');
    const [contentTypeTabIndex, setContentTypeTabIndex] = useState(0);
    const [showHiddenContentTypes, setShowHiddenContentTypes] = useState(false);
    const [showHiddenVariants, setShowHiddenVariants] = useState(false);
    const [contentTypeFormIsHidden, setContentTypeFormIsHidden] = useState(false);
    const [deletedContentCardDefaults, setDeletedContentCardDefaults] = useState(new Set());
    const [deletedDampingCoefficients, setDeletedDampingCoefficients] = useState(new Set());
    const [deletedEmployeePlans, setDeletedEmployeePlans] = useState(new Set());
    const [newContentCardDefaults, setNewContentCardDefaults] = useState([]);
    const [newDampingCoefficients, setNewDampingCoefficients] = useState([]);
    const [newEmployeePlans, setNewEmployeePlans] = useState([]);
    const modalContentRef = useRef(null);
    const validationMessageRef = useRef(null);
    const dampingToInputRefs = useRef({});
    const pendingDampingDraftsOverrideRef = useRef(null);
    const skipDampingGapsCheckRef = useRef(false);
    const pendingDampingGapsFixedDraftsRef = useRef(null);

    const contentViewPlan = useSelector((state) =>
      selectors.selectContentViewPlanForCurrentBoardByYearMonth(
        state,
        contentViewPlanYear,
        contentViewPlanMonth,
      ),
    );

    const currentBoard = useSelector(selectors.selectCurrentBoard);
    const currentBoardName = currentBoardNameProp || currentBoard?.name || '';

    const pointSystemId = pointSystem?.id;

    const boardsUsingTemplate = useSelector((state) =>
      selectors.selectBoardsUsingPointSystemTemplate(state, selectedTemplateIdProp || pointSystemId),
    );

    const visibleDampingCoefficients = useMemo(
      () =>
        (dampingCoefficients || []).filter(
          (dc) => !deletedDampingCoefficients.has(String(dc.id)),
        ),
      [dampingCoefficients, deletedDampingCoefficients],
    );

    const firstDampingItemId = useMemo(() => {
      if (visibleDampingCoefficients.length > 0) {
        return String(visibleDampingCoefficients[0].id);
      }
      if (newDampingCoefficients.length > 0) {
        return String(newDampingCoefficients[0].tempId);
      }
      return null;
    }, [visibleDampingCoefficients, newDampingCoefficients]);

    const DeletePopup = usePopup(DeleteStep);
    const RestorePopup = usePopup(ConfirmStep);

    useEffect(() => {
      if (open) {
        onEmployeePlansFetch();
        // Шаблоны глобальные — грузим независимо от наличия board PS
        onContentTypeTemplatesFetch();

        if (pointSystemId) {
          // Сначала синхронизируем типы с шаблоном, затем загружаем планы
          onContentTypesFetch();
          onContentCardDefaultsFetch();
          onDampingCoefficientsFetch();
          onContentViewPlansFetch(pointSystemId);
        }

        // Сбрасываем ошибки валидации при открытии модального окна
        setValidationErrors({});
        setDampingValidationErrors({});
        setEmployeePlanValidationErrors({});
        setEmployeePlanConflictErrors({});
        setShowValidationMessage(false);
        setSaveMessage(null);
        setShowCloseConfirmation(false);
        setShowDampingGapsConfirmation(false);
        setShowApplyToBoardConfirmation(false);
        pendingDampingDraftsOverrideRef.current = null;
        skipDampingGapsCheckRef.current = false;
        pendingDampingGapsFixedDraftsRef.current = null;
        // Сбрасываем состояния для новых и удаленных элементов
        setDeletedContentCardDefaults(new Set());
        setDeletedDampingCoefficients(new Set());
        setDeletedEmployeePlans(new Set());
        setNewContentCardDefaults([]);
        setNewDampingCoefficients([]);
        setNewEmployeePlans([]);
        setDraftsById({});
        setContentCardDefaultDraftsById({});
        setDampingDraftsById({});
        setContentCardDefaultsServerById({});
        setContentViewPlanDraftsByKey({});
        setContentViewPlanServerByKey({});
        setContentViewPlanDirtyByKey({});
        setContentTypeFormOpen(false);
        setContentTypeFormMode('create');
        setContentTypeFormId(null);
        setContentTypeFormTitle('');
        setContentTypeFormIcon('');
        setContentTypeTabIndex(0);
        setShowHiddenContentTypes(false);
        setShowHiddenVariants(false);
        setContentTypeFormIsHidden(false);
        setShowHiddenTemplates(false);
        setPendingSelectTemplateName(null);
      }
    }, [
      open,
      pointSystemId,
      onEmployeePlansFetch,
      onContentViewPlansFetch,
      onContentCardDefaultsFetch,
      onDampingCoefficientsFetch,
      onContentTypesFetch,
      onContentTypeTemplatesFetch,
    ]);

    useEffect(() => {
      if (!open) {
        return;
      }

      setNameDraft(pointSystem?.name ? String(pointSystem.name) : '');
      setNameError(false);
      setShiftsPerPlanDraft(String(resolveShiftsPerPlanValue(pointSystem?.shiftsPerPlan)));
      setShiftsPerPlanError(false);
    }, [open, pointSystemId, pointSystem?.name, pointSystem?.shiftsPerPlan]);

    const visibleContentTypes = useMemo(() => {
      const list = contentTypes || [];
      if (showHiddenContentTypes) {
        return list;
      }
      return list.filter((item) => !isHiddenEntity(item));
    }, [contentTypes, showHiddenContentTypes]);

    const hasHiddenContentTypes = useMemo(
      () => (contentTypes || []).some((item) => isHiddenEntity(item)),
      [contentTypes],
    );

    const contentTypeKeys = useMemo(
      () => (contentTypes || []).map((item) => item.key),
      [contentTypes],
    );

    const buildValuesFromPlan = useCallback(
      (plan) => {
        const next = {};
        contentTypeKeys.forEach((key) => {
          next[key] = getViewsBase(plan, key);
        });
        return next;
      },
      [contentTypeKeys],
    );

    const valuesEqual = useCallback((left = {}, right = {}) => {
      const keys = new Set([...Object.keys(left || {}), ...Object.keys(right || {})]);
      for (const key of keys) {
        if (Number(left[key]) !== Number(right[key])) {
          return false;
        }
      }
      return true;
    }, []);

    useEffect(() => {
      const isDirty = Boolean(contentViewPlanDirtyByKey[contentViewPlanKey]);
      const existingDraft = contentViewPlanDraftsByKey[contentViewPlanKey];

      if (isDirty && existingDraft) {
        setContentViewPlanForm((prev) => {
          if (valuesEqual(prev.values, existingDraft.values)) {
            return prev;
          }
          return existingDraft;
        });
        return;
      }

      if (!contentViewPlan) {
        const emptyValues = {};
        contentTypeKeys.forEach((key) => {
          emptyValues[key] = 0;
        });

        setContentViewPlanForm((prev) => {
          if (valuesEqual(prev.values, emptyValues)) {
            return prev;
          }
          return { values: emptyValues };
        });
        return;
      }

      const nextValues = buildValuesFromPlan(contentViewPlan);

      setContentViewPlanServerByKey((prev) => {
        const current = prev[contentViewPlanKey];
        if (
          current &&
          current.id === contentViewPlan.id &&
          valuesEqual(current.values, nextValues)
        ) {
          return prev;
        }

        return {
          ...prev,
          [contentViewPlanKey]: {
            id: contentViewPlan.id,
            values: nextValues,
          },
        };
      });

      setContentViewPlanDraftsByKey((prev) => {
        const current = prev[contentViewPlanKey];
        const next = { values: nextValues };

        if (current && valuesEqual(current.values, next.values)) {
          return prev;
        }

        return { ...prev, [contentViewPlanKey]: next };
      });

      setContentViewPlanForm((prev) => {
        if (valuesEqual(prev.values, nextValues)) {
          return prev;
        }
        return { values: nextValues };
      });
    }, [
      contentViewPlan,
      contentViewPlanDraftsByKey,
      contentViewPlanDirtyByKey,
      contentViewPlanKey,
      contentTypeKeys,
      buildValuesFromPlan,
      valuesEqual,
    ]);

    useEffect(() => {
      const maxIndex = Math.max(0, visibleContentTypes.length - 1);
      if (contentTypeTabIndex > maxIndex) {
        setContentTypeTabIndex(maxIndex);
      }
    }, [visibleContentTypes, contentTypeTabIndex]);

    const selectedTemplateId = selectedTemplateIdProp
      ? String(selectedTemplateIdProp)
      : null;

    useEffect(() => {
      if (!open || !selectedTemplateId) {
        return;
      }

      const selected = (contentTypeTemplates || []).find(
        (template) => String(template.id) === String(selectedTemplateId),
      );
      if (selected && isHiddenEntity(selected)) {
        setShowHiddenTemplates(true);
      }
    }, [open, selectedTemplateId, contentTypeTemplates]);

    const hasPointSystemIdentity =
      Boolean(String(nameDraft || '').trim()) || Boolean(selectedTemplateId);
    const canManageContentTypesInViewPlan =
      canEditPointSystemSettings && hasPointSystemIdentity;

    const visibleTemplates = useMemo(() => {
      const list = contentTypeTemplates || [];
      if (showHiddenTemplates) {
        return list;
      }
      return list.filter((template) => !isHiddenEntity(template));
    }, [contentTypeTemplates, showHiddenTemplates]);

    useEffect(() => {
      if (!pendingSelectTemplateName) {
        return;
      }

      const found = (contentTypeTemplates || []).find(
        (template) => String(template.name) === String(pendingSelectTemplateName),
      );
      if (!found) {
        return;
      }

      if (typeof onSelectTemplate === 'function') {
        onSelectTemplate(String(found.id));
      }
      setPendingSelectTemplateName(null);
    }, [contentTypeTemplates, pendingSelectTemplateName, onSelectTemplate]);

    const templateDeleteConfirmContent =
      'Шаблон будет скрыт и недоступен для выбора. Настройки досок и исторические расчёты не изменятся.';
    const templateRestoreConfirmContent = 'Шаблон снова станет доступен для выбора.';

    const handleSelectTemplate = useCallback(
      (templateId) => {
        if (!templateId || String(templateId) === String(selectedTemplateId || '')) {
          return;
        }
        if (typeof onSelectTemplate === 'function') {
          onSelectTemplate(String(templateId));
        }
      },
      [selectedTemplateId, onSelectTemplate],
    );

    const handleStartCreateTemplate = useCallback(() => {
      if (!canEditPointSystemSettings) {
        return;
      }

      const baseName = 'Новый шаблон';
      const existingNames = new Set(
        (contentTypeTemplates || []).map((template) => String(template.name || '')),
      );
      let name = baseName;
      let index = 2;
      while (existingNames.has(name)) {
        name = `${baseName} ${index}`;
        index += 1;
      }

      onContentTypeTemplateCreate({ name });
      setPendingSelectTemplateName(name);
    }, [canEditPointSystemSettings, contentTypeTemplates, onContentTypeTemplateCreate]);

    const handleTemplateDeleteRequest = useCallback((templateId, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!templateId) {
        return;
      }
      setTemplateIdToDelete(String(templateId));
    }, []);

    const handleTemplateDeleteConfirm = useCallback(() => {
      if (!templateIdToDelete) {
        return;
      }
      onContentTypeTemplateDelete(templateIdToDelete);
      if (String(selectedTemplateId || '') === String(templateIdToDelete)) {
        if (typeof onSelectTemplate === 'function') {
          onSelectTemplate(null);
        }
      }
      setTemplateIdToDelete(null);
    }, [templateIdToDelete, onContentTypeTemplateDelete, selectedTemplateId, onSelectTemplate]);

    const handleTemplateDeleteCancel = useCallback(() => {
      setTemplateIdToDelete(null);
    }, []);

    const handleTemplateRestore = useCallback((templateId, event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!templateId) {
        return;
      }
      setTemplateIdToRestore(String(templateId));
    }, []);

    const handleTemplateRestoreConfirm = useCallback(() => {
      if (!templateIdToRestore) {
        return;
      }
      onContentTypeTemplateUpdate(templateIdToRestore, { isHidden: false });
      setTemplateIdToRestore(null);
    }, [templateIdToRestore, onContentTypeTemplateUpdate]);

    const handleApplyToBoardRequest = useCallback(() => {
      if (!canEditPointSystemSettings || !selectedTemplateId || !currentBoardName) {
        return;
      }
      setShowApplyToBoardConfirmation(true);
    }, [canEditPointSystemSettings, selectedTemplateId, currentBoardName]);

    const handleApplyToBoardCancel = useCallback(() => {
      setShowApplyToBoardConfirmation(false);
    }, []);

    const isTemplateSwitch =
      Boolean(boardSourceTemplateId) &&
      Boolean(selectedTemplateId) &&
      String(boardSourceTemplateId) !== String(selectedTemplateId);

    const applyConfirmContent = isTemplateSwitch
      ? 'При смене шаблона закрытые задачи будут относиться к предыдущему шаблону'
      : `Применить настройки шаблона к доске «${currentBoardName}»? Закрытые задачи в списках будут пересчитаны по актуальным значениям шаблона.`;

    const handleTemplateRestoreCancel = useCallback(() => {
      setTemplateIdToRestore(null);
    }, []);

    useEffect(() => {
      const ids = new Set(employeePlans.map((plan) => String(plan.id)));

      setDraftsById((prev) => {
        let changed = false;
        let next = prev;

        employeePlans.forEach((plan) => {
          const id = String(plan.id);
          if (next[id]) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          next[id] = {
            from: plan.from ?? 0,
            to: plan.to ?? 0,
          };
        });

        Object.keys(next).forEach((id) => {
          // Не трогаем локально добавленные планы
          if (ids.has(id) || id.startsWith('new-')) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          delete next[id];
        });

        return changed ? next : prev;
      });
    }, [employeePlans]);

    const contentCardDefaults = useSelector((state) =>
      selectors.selectContentCardDefaultsForPointSystem(state, pointSystemId),
    );

    const contentCardDefaultsByType = useMemo(() => {
      const byType = {};
      visibleContentTypes.forEach((contentType) => {
        byType[contentType.key] = [];
      });

      contentCardDefaults.forEach((item) => {
        const id = String(item.id);
        if (deletedContentCardDefaults.has(id)) {
          return;
        }
        if (!showHiddenVariants && isHiddenEntity(item)) {
          return;
        }
        if (!byType[item.type]) {
          byType[item.type] = [];
        }
        byType[item.type].push(item);
      });

      newContentCardDefaults.forEach((newItem) => {
        if (!byType[newItem.type]) {
          byType[newItem.type] = [];
        }
        byType[newItem.type].push({
          id: newItem.tempId,
          type: newItem.type,
          name: '',
          value: 0,
          description: '',
        });
      });

      return byType;
    }, [
      contentCardDefaults,
      deletedContentCardDefaults,
      newContentCardDefaults,
      visibleContentTypes,
      showHiddenVariants,
    ]);

    const hasHiddenVariantsByType = useMemo(() => {
      const result = {};
      (contentCardDefaults || []).forEach((item) => {
        const id = String(item.id);
        if (deletedContentCardDefaults.has(id) || !isHiddenEntity(item)) {
          return;
        }
        result[item.type] = true;
      });
      return result;
    }, [contentCardDefaults, deletedContentCardDefaults]);

    useEffect(() => {
      const items = contentCardDefaults;
      const ids = new Set(items.map((item) => String(item.id)));

      // Keep server snapshot in sync with store (used for hasUnsavedChanges to avoid async-save prompt flicker)
      setContentCardDefaultsServerById((prev) => {
        let changed = false;
        let next = prev;

        items.forEach((item) => {
          const id = String(item.id);
          const current = next[id];
          const snapshot = {
            name: item.name ?? '',
            value: item.value ?? 0,
            description: item.description ?? '',
          };

          if (
            current &&
            current.name === snapshot.name &&
            current.value === snapshot.value &&
            current.description === snapshot.description
          ) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }
          next[id] = snapshot;
        });

        Object.keys(next).forEach((id) => {
          if (ids.has(id)) {
            return;
          }
          if (!changed) {
            next = { ...prev };
            changed = true;
          }
          delete next[id];
        });

        return changed ? next : prev;
      });

      setContentCardDefaultDraftsById((prev) => {
        let changed = false;
        let next = prev;

        // Add drafts for newly appeared items (do not overwrite existing drafts)
        items.forEach((item) => {
          const id = String(item.id);
          if (next[id]) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          next[id] = {
            name: item.name ?? '',
            value: item.value ?? 0,
            description: item.description ?? '',
          };
        });

        // Remove drafts for items that no longer exist
        Object.keys(next).forEach((id) => {
          if (ids.has(id)) {
            return;
          }

          if (!changed) {
            next = { ...prev };
            changed = true;
          }

          delete next[id];
        });

        return changed ? next : prev;
      });
    }, [contentCardDefaults]);

    // Прокручиваем модальное окно в начало при показе сообщения об ошибке
    useEffect(() => {
      if (showValidationMessage) {
        const scrollToTop = () => {
          // Ищем модальное окно через селектор Semantic UI
          const modal = document.querySelector('.ui.modal.active, .ui.modal.visible');
          if (!modal) {
            return;
          }
          
          // Сначала пробуем найти прокручиваемый элемент через сообщение
          if (validationMessageRef.current) {
            // Ищем ближайший прокручиваемый родитель сообщения
            let scrollableParent = validationMessageRef.current.parentElement;
            while (scrollableParent && scrollableParent !== modal) {
              const style = window.getComputedStyle(scrollableParent);
              if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && 
                  scrollableParent.scrollHeight > scrollableParent.clientHeight) {
                scrollableParent.scrollTop = 0;
                return;
              }
              scrollableParent = scrollableParent.parentElement;
            }
          }
          
          // Пробуем через ref напрямую
          if (modalContentRef.current) {
            const element = modalContentRef.current;
            if (element && typeof element.scrollTop !== 'undefined') {
              element.scrollTop = 0;
              return;
            }
          }
          
          // Ищем прокручиваемый контент - ищем все элементы внутри модального окна
          const allElements = Array.from(modal.querySelectorAll('*'));
          allElements.push(modal); // Добавляем сам модальный элемент
          
          for (const el of allElements) {
            const style = window.getComputedStyle(el);
            const hasOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';
            const isScrollable = el.scrollHeight > el.clientHeight;
            
            if (hasOverflow && isScrollable) {
              el.scrollTop = 0;
              return;
            }
          }
        };
        
        // Используем двойной setTimeout для гарантии, что DOM обновлен
        const timeoutId = setTimeout(() => {
          requestAnimationFrame(() => {
            scrollToTop();
          });
        }, 200);
        
        return () => clearTimeout(timeoutId);
      }
    }, [showValidationMessage]);

    const sortedEmployeePlans = useMemo(() => {
      // Фильтруем удаленные элементы
      const existingPlans = employeePlans
        .filter((plan) => !deletedEmployeePlans.has(String(plan.id)))
        .sort((a, b) => (a.sort || 0) - (b.sort || 0));
      
      // Добавляем новые элементы
      const allPlans = [
        ...existingPlans,
        ...newEmployeePlans.map((newItem) => ({
          id: newItem.tempId,
          sort: newItem.sort,
          from: 0,
          to: 0,
        })),
      ].sort((a, b) => (a.sort || 0) - (b.sort || 0));
      
      return allPlans;
    }, [employeePlans, deletedEmployeePlans, newEmployeePlans]);

    // Диапазоны планов сотрудников включительные ([from..to]), поэтому касание границ — конфликт.
    const checkEmployeePlanRangeConflict = useCallback((item1, item2) => {
      const from1 = item1.fromValue;
      const to1 = item1.toValue;
      const from2 = item2.fromValue;
      const to2 = item2.toValue;

      return from1 <= to2 && from2 <= to1;
    }, []);

    const validateEmployeePlanConflicts = useCallback((draftsOverride = null) => {
      const draftsToUse = draftsOverride || draftsById;
      const conflicts = {};
      const allItems = [];

      employeePlans.forEach((plan) => {
        const id = String(plan.id);
        if (deletedEmployeePlans.has(id)) {
          return;
        }
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }

        const fromValue = draft.from !== null && draft.from !== '' && draft.from !== undefined
          ? Number(draft.from)
          : null;
        const toValue = draft.to !== null && draft.to !== '' && draft.to !== undefined
          ? Number(draft.to)
          : null;

        if (
          fromValue !== null &&
          toValue !== null &&
          !Number.isNaN(fromValue) &&
          !Number.isNaN(toValue) &&
          fromValue <= toValue
        ) {
          allItems.push({ id, fromValue, toValue });
        }
      });

      newEmployeePlans.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }

        const fromValue = draft.from !== null && draft.from !== '' && draft.from !== undefined
          ? Number(draft.from)
          : null;
        const toValue = draft.to !== null && draft.to !== '' && draft.to !== undefined
          ? Number(draft.to)
          : null;

        if (
          fromValue !== null &&
          toValue !== null &&
          !Number.isNaN(fromValue) &&
          !Number.isNaN(toValue) &&
          fromValue <= toValue
        ) {
          allItems.push({ id, fromValue, toValue });
        }
      });

      for (let i = 0; i < allItems.length; i += 1) {
        const item1 = allItems[i];

        for (let j = i + 1; j < allItems.length; j += 1) {
          const item2 = allItems[j];

          if (checkEmployeePlanRangeConflict(item1, item2)) {
            conflicts[item1.id] = true;
            conflicts[item2.id] = true;
          }
        }
      }

      return conflicts;
    }, [employeePlans, draftsById, deletedEmployeePlans, newEmployeePlans, checkEmployeePlanRangeConflict]);

    const getDampingDraftValidationErrors = useCallback((draft, { requireTo }) => {
      if (!draft) {
        return null;
      }

      const errors = {};

      const isToInfinity = Boolean(draft.isInfinity);
      const isFromInfinity = Boolean(draft.isFromInfinity);
      const fromValue = !isFromInfinity && draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
      const toValue = !isToInfinity && draft.to !== null && draft.to !== '' ? Number(draft.to) : null;

      const isToMissing = !isToInfinity && (draft.to === null || draft.to === '' || typeof draft.to === 'undefined');

      // "до" обязательно для заполнения (только когда проверяем на сабмите)
      if (requireTo && isToMissing) {
        errors.to = true;
      }

      // Валидация диапазона: from не может быть больше to (только если оба значения заполнены и не бесконечность)
      if (
        !isToInfinity &&
        !isFromInfinity &&
        fromValue !== null &&
        toValue !== null &&
        !Number.isNaN(fromValue) &&
        !Number.isNaN(toValue) &&
        fromValue > toValue
      ) {
        errors.from = true;
        errors.to = true;
      }

      // "до (%)" не может быть меньше -100
      if (!isToInfinity && toValue !== null && !Number.isNaN(toValue) && toValue < -100) {
        errors.to = true;
      }

      // "от (%)" не может быть меньше -100 (только если не бесконечность)
      if (!isFromInfinity && fromValue !== null && !Number.isNaN(fromValue) && fromValue < -100) {
        errors.from = true;
      }

      // "Коэффициент" не может быть отрицательным
      const valueNum = draft.value !== null && draft.value !== '' ? Number(draft.value) : null;
      if (valueNum !== null && !Number.isNaN(valueNum) && valueNum < 0) {
        errors.value = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }, []);

    const contentViewPlanYearOptions = useMemo(() => {
      const currentYear = new Date().getFullYear();

      return Array.from({ length: 10 }, (_, i) => {
        const year = currentYear + i;
        return {
          key: year,
          text: `${year}`,
          value: year,
        };
      });
    }, []);

    const contentViewPlanMonthTitles = useMemo(
      () => [
        'Январь',
        'Февраль',
        'Март',
        'Апрель',
        'Май',
        'Июнь',
        'Июль',
        'Август',
        'Сентябрь',
        'Октябрь',
        'Ноябрь',
        'Декабрь',
      ],
      [],
    );

    const filledContentViewPlanMonths = useMemo(() => {
      const filled = new Set();
      const typeKeys = (contentTypes || []).map((item) => item.key);
      const plansByMonth = {};

      (contentViewPlans || []).forEach((plan) => {
        if (Number(plan.year) !== Number(contentViewPlanYear)) {
          return;
        }
        plansByMonth[Number(plan.month)] = plan;
      });

      for (let month = 1; month <= 12; month += 1) {
        const key = `${contentViewPlanYear}-${month}`;
        const isDirty = Boolean(contentViewPlanDirtyByKey[key]);
        const draft = contentViewPlanDraftsByKey[key];

        // Учитываем только реально изменённые черновики или сохранённые ненулевые планы
        if (isDirty && draft) {
          if (hasPositiveContentViewPlanValues(draft.values)) {
            filled.add(month);
          }
          continue;
        }

        if (isContentViewPlanFilled(plansByMonth[month], typeKeys)) {
          filled.add(month);
        }
      }

      return filled;
    }, [
      contentTypes,
      contentViewPlans,
      contentViewPlanYear,
      contentViewPlanDraftsByKey,
      contentViewPlanDirtyByKey,
    ]);

    const handleDraftChange = useCallback((id, field, value) => {
      let nextValue = value;
      if (nextValue !== '' && nextValue !== null && typeof nextValue !== 'undefined') {
        // Разрешаем промежуточный ввод знака «минус»
        if (nextValue !== '-') {
          const num = Number(nextValue);
          if (!Number.isNaN(num)) {
            nextValue = String(Math.trunc(num));
          } else {
            // Нечисловой ввод (e, +, точки без цифр и т.п.) — игнорируем
            return;
          }
        }
      }

      setDraftsById((prev) => {
        const nextDrafts = {
          ...prev,
          [id]: {
            ...prev[id],
            [field]: nextValue,
          },
        };

        // Проверяем конфликты диапазонов сразу при изменении
        const conflicts = validateEmployeePlanConflicts(nextDrafts);
        setEmployeePlanConflictErrors(conflicts);

        return nextDrafts;
      });

      // Сбрасываем ошибку валидации для этого поля
      setEmployeePlanValidationErrors((prev) => {
        if (!prev[id]) {
          return prev;
        }
        const next = { ...prev };
        next[id] = {
          ...next[id],
          [field]: false,
        };
        // Если оба поля без ошибок, удаляем запись из errors
        if (!next[id].from && !next[id].to) {
          delete next[id];
        }
        return Object.keys(next).length > 0 ? next : {};
      });

      // Скрываем сообщение об ошибке, если все ошибки исправлены
      if (showValidationMessage) {
        setShowValidationMessage(false);
      }
    }, [showValidationMessage, validateEmployeePlanConflicts]);

    const handleShiftsPerPlanChange = useCallback((e) => {
      const normalized = normalizeShiftsPerPlanInput(e.target.value);

      if (normalized !== '' && !SHIFTS_PER_PLAN_TYPING_PATTERN.test(normalized)) {
        return;
      }

      setShiftsPerPlanDraft(normalized);
      setShiftsPerPlanError(false);

      if (showValidationMessage) {
        setShowValidationMessage(false);
      }
    }, [showValidationMessage]);

    const handleNameChange = useCallback(
      (e) => {
        setNameDraft(e.target.value);
        setNameError(false);

        if (showValidationMessage) {
          setShowValidationMessage(false);
        }
      },
      [showValidationMessage],
    );

    // Проверяем конфликты планов сотрудников при изменении drafts
    useEffect(() => {
      const conflicts = validateEmployeePlanConflicts();
      setEmployeePlanConflictErrors(conflicts);
    }, [draftsById, validateEmployeePlanConflicts]);

    const handlePlanDelete = useCallback(
      (id) => {
        // Добавляем в список удаленных (не удаляем сразу на сервере)
        setDeletedEmployeePlans((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewEmployeePlans((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
        // Сбрасываем ошибки валидации/конфликтов для удалённого элемента
        setEmployeePlanValidationErrors((prev) => {
          if (!prev[id]) {
            return prev;
          }
          const next = { ...prev };
          delete next[id];
          return Object.keys(next).length > 0 ? next : {};
        });
      },
      [],
    );

    const handlePlanAdd = useCallback(() => {
      if (!pointSystemId) {
        return;
      }
      // Создаем только в локальном состоянии
      const tempId = `new-ep-${Date.now()}-${Math.random()}`;
      const maxSort = employeePlans.length > 0 
        ? Math.max(...employeePlans.map((p) => p.sort || 0))
        : 0;
      const newItem = {
        tempId,
        sort: maxSort + 1,
        from: 0,
        to: 0,
      };
      setNewEmployeePlans((prev) => [...prev, newItem]);
      setDraftsById((prev) => ({
        ...prev,
        [tempId]: {
          from: 0,
          to: 0,
        },
      }));
    }, [pointSystemId, employeePlans]);

    const handleContentViewPlanYearChange = useCallback((_, { value }) => {
      setContentViewPlanYear(value);
      setContentViewPlanMonth(1);
    }, []);

    const handleContentViewPlanMonthClick = useCallback((month) => {
      setContentViewPlanMonth(month);
    }, []);

    const handleContentViewPlanFieldChange = useCallback((field, value) => {
      let nextValue = value;
      if (nextValue !== '' && nextValue !== null && typeof nextValue !== 'undefined') {
        const num = Number(nextValue);
        if (!Number.isNaN(num) && num < 0) {
          nextValue = '0';
        }
      }
      setContentViewPlanForm((prev) => {
        const next = {
          ...prev,
          values: {
            ...(prev.values || {}),
            [field]: nextValue,
          },
        };

        setContentViewPlanDraftsByKey((prevDrafts) => ({
          ...prevDrafts,
          [contentViewPlanKey]: next,
        }));

        return next;
      });

      setContentViewPlanDirtyByKey((prev) => {
        if (prev[contentViewPlanKey] === true) {
          return prev;
        }
        return { ...prev, [contentViewPlanKey]: true };
      });
    }, [contentViewPlanKey]);

    // Вспомогательная функция для проверки конфликта двух диапазонов
    const checkRangeConflict = useCallback((item1, item2) => {
      const from1 = item1.isFromInfinity ? -Infinity : (item1.fromValue ?? -Infinity);
      const to1 = item1.isToInfinity ? Infinity : (item1.toValue ?? Infinity);
      const from2 = item2.isFromInfinity ? -Infinity : (item2.fromValue ?? -Infinity);
      const to2 = item2.isToInfinity ? Infinity : (item2.toValue ?? Infinity);
      
      // Диапазоны считаем конфликтующими, только если есть пересечение с ненулевой "длиной".
      // Касание границ (например [0..1] и [1..2]) НЕ считаем конфликтом для непрерывной шкалы.
      return (from1 < to2) && (from2 < to1);
    }, []);

    // Функция для проверки конфликтов диапазонов
    const validateDampingCoefficientConflicts = useCallback((draftsOverride = null) => {
      const draftsToUse = draftsOverride || dampingDraftsById;
      const conflicts = {};
      
      // Собираем все активные элементы (существующие + новые, исключая удаленные)
      const allItems = [];
      
      // Существующие элементы
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        if (deletedDampingCoefficients.has(id)) {
          return;
        }
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }
        const isFromInfinity = draft.isFromInfinity;
        const fromValue = !isFromInfinity && draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
        const isToInfinity = draft.isInfinity;
        const toValue = isToInfinity 
          ? INFINITY_VALUE 
          : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
        
        if (fromValue !== null || toValue !== null || isFromInfinity || isToInfinity) {
          allItems.push({ id, fromValue, toValue, isToInfinity, isFromInfinity });
        }
      });
      
      // Новые элементы
      newDampingCoefficients.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }
        const isFromInfinity = draft.isFromInfinity;
        const fromValue = !isFromInfinity && draft.from !== null && draft.from !== '' ? Number(draft.from) : null;
        const isToInfinity = draft.isInfinity;
        const toValue = isToInfinity 
          ? INFINITY_VALUE 
          : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
        
        if (fromValue !== null || toValue !== null || isFromInfinity || isToInfinity) {
          allItems.push({ id, fromValue, toValue, isToInfinity, isFromInfinity });
        }
      });
      
      // Проверяем конфликты между всеми парами элементов
      for (let i = 0; i < allItems.length; i++) {
        const item1 = allItems[i];
        
        for (let j = i + 1; j < allItems.length; j++) {
          const item2 = allItems[j];
          
          // Проверяем перекрытие диапазонов
          const hasConflict = checkRangeConflict(item1, item2);
          
          if (hasConflict) {
            // Если у item1 бесконечность, то конфликт на стороне item1
            if (item1.isToInfinity) {
              conflicts[item1.id] = true;
            }
            // Если у item2 бесконечность, то конфликт на стороне item2
            if (item2.isToInfinity) {
              conflicts[item2.id] = true;
            }
            // Если оба не бесконечность, но диапазоны перекрываются
            if (!item1.isToInfinity && !item2.isToInfinity) {
              // Конфликт у обоих, если диапазоны перекрываются
              conflicts[item1.id] = true;
              conflicts[item2.id] = true;
            }
          }
        }
      }
      
      return conflicts;
    }, [dampingCoefficients, dampingDraftsById, deletedDampingCoefficients, newDampingCoefficients, checkRangeConflict]);

    const handleDampingFieldChange = useCallback((id, field, value) => {
      if (field === 'from' && firstDampingItemId && String(id) === firstDampingItemId) {
        return;
      }

      setDampingDraftsById((prev) => {
        const currentDraft = prev[id] || {};
        const nextDraft = {
          ...currentDraft,
          [field]: value,
        };
        
        // При вводе значения в поле "from" сбрасываем флаг isFromInfinity
        if (field === 'from' && value !== null && value !== '') {
          nextDraft.isFromInfinity = false;
        }
        
        // При вводе значения в поле "to" сбрасываем флаг isInfinity
        if (field === 'to' && value !== null && value !== '') {
          nextDraft.isInfinity = false;
        }
        
        // Валидация (для полей from/to): диапазон + (на сабмите) обязательность "до"
        if (field === 'from' || field === 'to') {
          const nextErrorsForDraft = getDampingDraftValidationErrors(nextDraft, { requireTo: showValidationMessage });

          setDampingValidationErrors((prevErrors) => {
            const nextErrors = { ...prevErrors };
            if (nextErrorsForDraft) {
              nextErrors[id] = nextErrorsForDraft;
            } else if (nextErrors[id]) {
              delete nextErrors[id];
            }
            return Object.keys(nextErrors).length > 0 ? nextErrors : {};
          });
        }
        
        const nextDrafts = {
          ...prev,
          [id]: nextDraft,
        };
        
        // Проверяем конфликты с новыми данными сразу
        const conflicts = validateDampingCoefficientConflicts(nextDrafts);
        setDampingConflictErrors(conflicts);
        
        return nextDrafts;
      });
    }, [firstDampingItemId, validateDampingCoefficientConflicts, getDampingDraftValidationErrors, showValidationMessage]);
    
    // Проверяем конфликты при изменении dampingDraftsById
    useEffect(() => {
      const conflicts = validateDampingCoefficientConflicts();
      setDampingConflictErrors(conflicts);
    }, [dampingDraftsById, validateDampingCoefficientConflicts]);

    const handleDampingInfinityToggle = useCallback((id, checked) => {
      setDampingDraftsById((prev) => {
        const currentDraft = prev[id] || {};
        const nextDrafts = {
          ...prev,
          [id]: {
            ...currentDraft,
            isInfinity: checked,
            to: checked ? null : (currentDraft.to ?? null),
          },
        };
        
        // Проверяем конфликты с новыми данными сразу
        const conflicts = validateDampingCoefficientConflicts(nextDrafts);
        setDampingConflictErrors(conflicts);
        
        return nextDrafts;
      });
      
      // Сбрасываем/пересчитываем ошибки валидации при переключении бесконечности
      setDampingValidationErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };

        // Если включили бесконечность — ошибки для "до" больше не актуальны
        if (checked) {
          if (nextErrors[id]) {
            delete nextErrors[id];
          }
          return Object.keys(nextErrors).length > 0 ? nextErrors : {};
        }

        // Если выключили бесконечность — оставляем/пересчитываем ошибки только если уже показываем ошибки (после сабмита)
        if (showValidationMessage) {
          const draft = dampingDraftsById[id] || {};
          const nextDraft = { ...draft, isInfinity: false };
          const nextErrorsForDraft = getDampingDraftValidationErrors(nextDraft, { requireTo: true });

          if (nextErrorsForDraft) {
            nextErrors[id] = nextErrorsForDraft;
          } else if (nextErrors[id]) {
            delete nextErrors[id];
          }
        }

        return Object.keys(nextErrors).length > 0 ? nextErrors : {};
      });
    }, [validateDampingCoefficientConflicts, showValidationMessage, dampingDraftsById, getDampingDraftValidationErrors]);

    const handleDampingFromInfinityToggle = useCallback((id, checked) => {
      if (firstDampingItemId && String(id) === firstDampingItemId) {
        return;
      }

      setDampingDraftsById((prev) => {
        const currentDraft = prev[id] || {};
        const nextDrafts = {
          ...prev,
          [id]: {
            ...currentDraft,
            isFromInfinity: checked,
            from: checked ? null : (currentDraft.from ?? null),
          },
        };
        
        // Проверяем конфликты с новыми данными сразу
        const conflicts = validateDampingCoefficientConflicts(nextDrafts);
        setDampingConflictErrors(conflicts);
        
        return nextDrafts;
      });
      
      // Сбрасываем/пересчитываем ошибки валидации при переключении бесконечности
      setDampingValidationErrors((prevErrors) => {
        const nextErrors = { ...prevErrors };

        // Если включили бесконечность — ошибки для "от" больше не актуальны
        if (checked) {
          if (nextErrors[id]) {
            const updatedErrors = { ...nextErrors[id] };
            delete updatedErrors.from;
            if (Object.keys(updatedErrors).length > 0) {
              nextErrors[id] = updatedErrors;
            } else {
              delete nextErrors[id];
            }
          }
          return Object.keys(nextErrors).length > 0 ? nextErrors : {};
        }

        // Если выключили бесконечность — оставляем/пересчитываем ошибки только если уже показываем ошибки (после сабмита)
        if (showValidationMessage) {
          const draft = dampingDraftsById[id] || {};
          const nextDraft = { ...draft, isFromInfinity: false };
          const nextErrorsForDraft = getDampingDraftValidationErrors(nextDraft, { requireTo: true });

          if (nextErrorsForDraft) {
            nextErrors[id] = nextErrorsForDraft;
          } else if (nextErrors[id]) {
            delete nextErrors[id];
          }
        }

        return Object.keys(nextErrors).length > 0 ? nextErrors : {};
      });
    }, [firstDampingItemId, validateDampingCoefficientConflicts, showValidationMessage, dampingDraftsById, getDampingDraftValidationErrors]);

    useEffect(() => {
      if (!dampingCoefficients || dampingCoefficients.length === 0) {
        setDampingDraftsById({});
        return;
      }

      const nextDrafts = {};
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        // Преобразуем бесконечность (1000000000) в null для UI
        nextDrafts[id] = {
          from: isFromInfinity(dc.from) ? null : (dc.from ?? null),
          to: isInfinity(dc.to) ? null : (dc.to ?? null),
          value: dc.value ?? 1,
          isInfinity: isInfinity(dc.to), // Флаг для UI
          isFromInfinity: isFromInfinity(dc.from), // Флаг для UI поля "От"
        };
      });

      setDampingDraftsById((prev) => {
        // Обновляем только если данные изменились
        const hasChanges = Object.keys(nextDrafts).some(
          (id) =>
            prev[id]?.from !== nextDrafts[id].from ||
            prev[id]?.to !== nextDrafts[id].to ||
            prev[id]?.value !== nextDrafts[id].value ||
            prev[id]?.isInfinity !== nextDrafts[id].isInfinity ||
            prev[id]?.isFromInfinity !== nextDrafts[id].isFromInfinity,
        );

        return hasChanges ? nextDrafts : prev;
      });
    }, [dampingCoefficients]);

    // У первого элемента диапазона «от (%)» всегда фиксировано -100
    useEffect(() => {
      if (!firstDampingItemId) {
        return;
      }

      setDampingDraftsById((prev) => {
        const draft = prev[firstDampingItemId];
        if (!draft) {
          return prev;
        }

        const fromIsFixed =
          !draft.isFromInfinity &&
          (draft.from === -100 || draft.from === '-100' || Number(draft.from) === -100);

        if (fromIsFixed) {
          return prev;
        }

        return {
          ...prev,
          [firstDampingItemId]: {
            ...draft,
            from: -100,
            isFromInfinity: false,
          },
        };
      });
    }, [firstDampingItemId, dampingCoefficients, newDampingCoefficients]);

    const handleDampingCoefficientDelete = useCallback(
      (id) => {
        // Добавляем в список удаленных (не удаляем сразу на сервере)
        setDeletedDampingCoefficients((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setDampingDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewDampingCoefficients((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
      },
      [],
    );

    const handleDampingCoefficientCreate = useCallback(() => {
      if (!pointSystemId) {
        return;
      }
      // Создаем только в локальном состоянии
      const tempId = `new-${Date.now()}-${Math.random()}`;
      const isFirst =
        visibleDampingCoefficients.length === 0 && newDampingCoefficients.length === 0;
      const newItem = {
        tempId,
        from: isFirst ? -100 : null,
        to: null,
        value: 1,
      };
      setNewDampingCoefficients((prev) => [...prev, newItem]);
      setDampingDraftsById((prev) => ({
        ...prev,
        [tempId]: {
          from: isFirst ? -100 : null,
          to: null,
          value: 1,
          isInfinity: false,
          isFromInfinity: false,
        },
      }));
    }, [pointSystemId, visibleDampingCoefficients.length, newDampingCoefficients.length]);

    const handleContentCardDefaultDraftChange = useCallback((id, field, value) => {
      let nextValue = value;
      if (field === 'value') {
        if (nextValue !== '' && nextValue !== null && typeof nextValue !== 'undefined') {
          const num = Number(nextValue);
          if (!Number.isNaN(num) && num < 0) {
            nextValue = '0';
          }
        }
      }
      setContentCardDefaultDraftsById((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: nextValue,
        },
      }));

      // Сбрасываем ошибку валидации для этого поля
      setValidationErrors((prev) => {
        if (!prev[id]) {
          return prev;
        }
        const next = { ...prev };
        next[id] = {
          ...next[id],
          [field]: false,
        };
        // Если оба поля без ошибок, удаляем запись из errors
        if (!next[id].name && !next[id].value) {
          delete next[id];
        }
        return Object.keys(next).length > 0 ? next : {};
      });

      // Скрываем сообщение об ошибке, если все ошибки исправлены
      if (showValidationMessage) {
        setShowValidationMessage(false);
      }
    }, [showValidationMessage]);

    const handleContentCardDefaultDelete = useCallback(
      (id) => {
        const idStr = String(id);
        // Новые (ещё не сохранённые) — только локально
        if (idStr.startsWith('new-')) {
          setDeletedContentCardDefaults((prev) => new Set([...prev, idStr]));
          setContentCardDefaultDraftsById((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          setNewContentCardDefaults((prev) =>
            prev.filter((item) => String(item.tempId) !== idStr),
          );
          return;
        }

        // Существующие — soft-hide сразу на сервере
        onContentCardDefaultDelete(id);
        setContentCardDefaultDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      },
      [onContentCardDefaultDelete],
    );

    const handleContentCardDefaultRestore = useCallback(
      (id) => {
        onContentCardDefaultUpdate(id, { isHidden: false });
      },
      [onContentCardDefaultUpdate],
    );

    // Функция для создания нового ContentCardDefault только в локальном состоянии
    const handleContentCardDefaultCreate = useCallback(
      (type) => {
        if (!pointSystemId) {
          return;
        }
        // Создаем только в локальном состоянии
        const tempId = `new-ccd-${Date.now()}-${Math.random()}`;
        const newItem = {
          tempId,
          type,
          name: '',
          value: 0,
          description: '',
        };
        setNewContentCardDefaults((prev) => [...prev, newItem]);
        setContentCardDefaultDraftsById((prev) => ({
          ...prev,
          [tempId]: {
            name: '',
            value: 0,
            description: '',
          },
        }));
      },
      [pointSystemId],
    );

    const validateContentCardDefaults = useCallback(() => {
      const errors = {};
      const idsToDelete = [];

      // Валидируем существующие элементы
      contentCardDefaults.forEach((item) => {
        const id = String(item.id);
        // Пропускаем удаленные и скрытые
        if (deletedContentCardDefaults.has(id) || isHiddenEntity(item)) {
          return;
        }
        const draft = contentCardDefaultDraftsById[id];
        
        if (!draft) {
          return;
        }

        const name = draft.name;
        const value = draft.value;

        const isNameEmpty = !name || name === '' || name === undefined || name === null;
        const isValueEmpty = value === null || value === undefined || value === 0 || value === '0' || value === '';

        if (isNameEmpty && isValueEmpty) {
          // Оба поля пустые - нужно удалить
          idsToDelete.push(id);
        } else if (isNameEmpty || isValueEmpty) {
          // Одно поле пустое - ошибка валидации
          errors[id] = {
            name: isNameEmpty,
            value: isValueEmpty,
          };
        }
      });

      // Валидируем новые элементы
      newContentCardDefaults.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = contentCardDefaultDraftsById[id];
        
        if (!draft) {
          return;
        }

        const name = draft.name;
        const value = draft.value;

        const isNameEmpty = !name || name === '' || name === undefined || name === null;
        const isValueEmpty = value === null || value === undefined || value === 0 || value === '0' || value === '';

        if (isNameEmpty && isValueEmpty) {
          // Оба поля пустые - удаляем из новых
          idsToDelete.push(id);
        } else if (isNameEmpty || isValueEmpty) {
          // Одно поле пустое - ошибка валидации
          errors[id] = {
            name: isNameEmpty,
            value: isValueEmpty,
          };
        }
      });

      return { errors, idsToDelete };
    }, [contentCardDefaults, contentCardDefaultDraftsById, deletedContentCardDefaults, newContentCardDefaults]);

    const validateEmployeePlans = useCallback(() => {
      const errors = {};
      const idsToDelete = [];

      // Валидируем существующие элементы
      employeePlans.forEach((plan) => {
        const id = String(plan.id);
        // Пропускаем удаленные
        if (deletedEmployeePlans.has(id)) {
          return;
        }
        const draft = draftsById[id];
        
        if (!draft) {
          return;
        }

        const fromValue = draft.from !== null && draft.from !== '' && draft.from !== undefined ? Number(draft.from) : null;
        const toValue = draft.to !== null && draft.to !== '' && draft.to !== undefined ? Number(draft.to) : null;

        const isFromEmpty = fromValue === null || Number.isNaN(fromValue);
        const isToEmpty = toValue === null || Number.isNaN(toValue);

        if (isFromEmpty && isToEmpty) {
          // Оба поля пустые - нужно удалить
          idsToDelete.push(id);
        } else if (isFromEmpty || isToEmpty) {
          // Одно поле пустое - ошибка валидации
          errors[id] = {
            from: isFromEmpty,
            to: isToEmpty,
          };
        } else if (fromValue !== null && toValue !== null && fromValue > toValue) {
          // "От" больше "До" - ошибка валидации
          errors[id] = {
            from: true,
            to: true,
          };
        }
      });

      // Валидируем новые элементы
      newEmployeePlans.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = draftsById[id];
        
        if (!draft) {
          return;
        }

        const fromValue = draft.from !== null && draft.from !== '' && draft.from !== undefined ? Number(draft.from) : null;
        const toValue = draft.to !== null && draft.to !== '' && draft.to !== undefined ? Number(draft.to) : null;

        const isFromEmpty = fromValue === null || Number.isNaN(fromValue);
        const isToEmpty = toValue === null || Number.isNaN(toValue);

        if (isFromEmpty && isToEmpty) {
          // Оба поля пустые - удаляем из новых
          idsToDelete.push(id);
        } else if (isFromEmpty || isToEmpty) {
          // Одно поле пустое - ошибка валидации
          errors[id] = {
            from: isFromEmpty,
            to: isToEmpty,
          };
        } else if (fromValue !== null && toValue !== null && fromValue > toValue) {
          // "От" больше "До" - ошибка валидации
          errors[id] = {
            from: true,
            to: true,
          };
        }
      });

      return { errors, idsToDelete };
    }, [employeePlans, draftsById, deletedEmployeePlans, newEmployeePlans]);

    const validateDampingCoefficients = useCallback((draftsOverride = null) => {
      const draftsToUse = draftsOverride || dampingDraftsById;
      const errors = {};

      // Валидируем существующие элементы
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        // Пропускаем удаленные
        if (deletedDampingCoefficients.has(id)) {
          return;
        }
        const draft = draftsToUse[id];
        
        if (!draft) {
          return;
        }

        const nextErrorsForDraft = getDampingDraftValidationErrors(draft, { requireTo: true });
        if (nextErrorsForDraft) {
          errors[id] = nextErrorsForDraft;
        }
      });

      // Валидируем новые элементы
      newDampingCoefficients.forEach((newItem) => {
        const id = String(newItem.tempId);
        const draft = draftsToUse[id];
        
        if (!draft) {
          return;
        }

        const nextErrorsForDraft = getDampingDraftValidationErrors(draft, { requireTo: true });
        if (nextErrorsForDraft) {
          errors[id] = nextErrorsForDraft;
        }
      });

      return errors;
    }, [
      dampingCoefficients,
      dampingDraftsById,
      deletedDampingCoefficients,
      newDampingCoefficients,
      getDampingDraftValidationErrors,
    ]);

    const buildDampingGapsFixIfNeeded = useCallback((draftsOverride = null) => {
      const draftsToUse = draftsOverride || dampingDraftsById;

      const allItems = [];
      const numberStringsForPrecision = [];

      const pushItem = (id) => {
        const draft = draftsToUse[id];
        if (!draft) {
          return;
        }

        const isFromInfinity = Boolean(draft.isFromInfinity);
        const isToInfinity = Boolean(draft.isInfinity);
        const fromRaw = isFromInfinity ? null : draft.from;
        const toRaw = isToInfinity ? null : draft.to;

        // На момент вызова "до" уже обязательно заполнено (мы вызываем после validateDampingCoefficients)
        if (!isToInfinity && (toRaw === null || toRaw === '' || typeof toRaw === 'undefined')) {
          return;
        }

        if (!isFromInfinity && fromRaw !== null && fromRaw !== '' && typeof fromRaw !== 'undefined') {
          numberStringsForPrecision.push(String(fromRaw));
        }
        if (!isToInfinity && toRaw !== null && toRaw !== '' && typeof toRaw !== 'undefined') {
          numberStringsForPrecision.push(String(toRaw));
        }

        const fromValue = isFromInfinity 
          ? -Infinity 
          : (fromRaw !== null && fromRaw !== '' && typeof fromRaw !== 'undefined' ? Number(fromRaw) : null);
        const toValue = isToInfinity ? Infinity : Number(toRaw);

        if (!isToInfinity && (toValue === null || Number.isNaN(toValue))) {
          return;
        }

        allItems.push({
          id,
          fromValue: fromValue !== null && !Number.isNaN(fromValue) ? fromValue : null,
          toValue,
          isToInfinity,
          isFromInfinity,
        });
      };

      // Существующие элементы
      dampingCoefficients.forEach((dc) => {
        const id = String(dc.id);
        if (deletedDampingCoefficients.has(id)) {
          return;
        }
        pushItem(id);
      });

      // Новые элементы
      newDampingCoefficients.forEach((newItem) => {
        const id = String(newItem.tempId);
        pushItem(id);
      });

      if (allItems.length <= 1) {
        return { hasGaps: false, fixedDraftsById: null };
      }

      // percent на бэкенде — вещественный, и мы трактуем диапазоны как непрерывные интервалы.
      // При этом точность "сетки" берём из введённых пользователем значений, чтобы устойчиво сравнивать числа.
      const maxDecimals = Math.min(
        6,
        numberStringsForPrecision.reduce((acc, s) => Math.max(acc, getDecimalPlacesFromString(s)), 0),
      );
      const scale = 10 ** maxDecimals;
      const startFromNorm = Math.round(-100 * scale); // percent не бывает меньше -100

      const normalizedItems = allItems
        .map((item) => {
          const fromNorm = item.isFromInfinity 
            ? -Infinity 
            : (item.fromValue === null ? startFromNorm : Math.max(startFromNorm, Math.round(item.fromValue * scale)));
          const toNorm = item.isToInfinity ? null : Math.max(startFromNorm, Math.round(item.toValue * scale));

          return {
            ...item,
            fromNorm,
            toNorm,
          };
        })
        .sort((a, b) => {
          if (a.fromNorm !== b.fromNorm) {
            return a.fromNorm - b.fromNorm;
          }
          return String(a.id).localeCompare(String(b.id));
        });

      let expectedFromNorm = startFromNorm;
      let hasGaps = false;
      let nextDraftsById = null;

      for (let i = 0; i < normalizedItems.length; i += 1) {
        const item = normalizedItems[i];

        // Пропускаем элементы с бесконечностью "от" при проверке зазоров
        if (!item.isFromInfinity && item.fromNorm > expectedFromNorm) {
          hasGaps = true;
          if (!nextDraftsById) {
            nextDraftsById = { ...draftsToUse };
          }

          const currentDraft = nextDraftsById[item.id] || {};
          nextDraftsById[item.id] = {
            ...currentDraft,
            from: formatScaledIntToInput(expectedFromNorm, scale, maxDecimals),
            isFromInfinity: false,
          };
        }

        if (item.isToInfinity) {
          break;
        }

        // Следующий диапазон должен начинаться ровно с предыдущего "до" (непрерывная шкала)
        expectedFromNorm = item.toNorm ?? expectedFromNorm;
      }

      return { hasGaps, fixedDraftsById: hasGaps ? nextDraftsById : null };
    }, [
      dampingCoefficients,
      dampingDraftsById,
      deletedDampingCoefficients,
      newDampingCoefficients,
    ]);

    // Функция для проверки наличия изменений в данных
    const hasUnsavedChanges = useCallback(() => {
      // Проверяем изменения в ContentCardDefaults
      const hasContentCardDefaultChanges = contentCardDefaults.some((item) => {
        const id = String(item.id);
        if (isHiddenEntity(item)) {
          return false;
        }
        if (deletedContentCardDefaults.has(id)) {
          return true;
        }
        const draft = contentCardDefaultDraftsById[id];
        if (draft) {
          const original = contentCardDefaultsServerById[id] || {
            name: item.name ?? '',
            value: item.value ?? 0,
            description: item.description ?? '',
          };

          const draftValue = Number(draft.value) || 0;
          const originalValue = Number(original.value) || 0;

          return (
            draft.name !== original.name ||
            draftValue !== originalValue ||
            draft.description !== original.description
          );
        }
        return false;
      });

      // Проверяем новые ContentCardDefaults
      const hasNewContentCardDefaults = newContentCardDefaults.length > 0;

      // Проверяем изменения в DampingCoefficients
      const hasDampingChanges = dampingCoefficients.some((dc) => {
        const id = String(dc.id);
        if (deletedDampingCoefficients.has(id)) {
          return true;
        }
        const draft = dampingDraftsById[id];
        if (draft) {
          const draftFrom = draft.isFromInfinity 
            ? INFINITY_VALUE 
            : (draft.from !== null && draft.from !== '' ? Number(draft.from) : null);
          const draftTo = draft.isInfinity 
            ? INFINITY_VALUE 
            : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
          const draftValue = Number(draft.value) || 1;
          const originalFrom = isFromInfinity(dc.from) ? INFINITY_VALUE : (dc.from !== null && dc.from !== '' ? Number(dc.from) : null);
          const originalTo = isInfinity(dc.to) ? INFINITY_VALUE : (dc.to !== null && dc.to !== '' ? Number(dc.to) : null);
          const originalValue = Number(dc.value) || 1;
          
          return (
            draftFrom !== originalFrom ||
            draftTo !== originalTo ||
            draftValue !== originalValue
          );
        }
        return false;
      });

      // Проверяем новые DampingCoefficients
      const hasNewDampingCoefficients = newDampingCoefficients.length > 0;

      // Проверяем изменения в ContentViewPlan (только dirty-черновики)
      const hasContentViewPlanChanges = Object.keys(contentViewPlanDirtyByKey).some((key) => {
        if (!contentViewPlanDirtyByKey[key]) {
          return false;
        }

        const draft = contentViewPlanDraftsByKey[key];
        if (!draft) {
          return false;
        }

        const server = contentViewPlanServerByKey[key];
        if (!server || !server.id) {
          return true;
        }

        const draftValues = draft.values || {};
        const serverValues = server.values || {};
        const keys = new Set([...Object.keys(draftValues), ...Object.keys(serverValues)]);
        return Array.from(keys).some(
          (typeKey) => Number(draftValues[typeKey] || 0) !== Number(serverValues[typeKey] || 0),
        );
      });

      // Проверяем изменения в EmployeePlans
      const hasEmployeePlanChanges = employeePlans.some((plan) => {
        const id = String(plan.id);
        if (deletedEmployeePlans.has(id)) {
          return true;
        }
        const draft = draftsById[id];
        if (draft) {
          const draftFrom = Math.trunc(Number(draft.from)) || 0;
          const draftTo = Math.trunc(Number(draft.to)) || 0;
          return draftFrom !== (plan.from ?? 0) || draftTo !== (plan.to ?? 0);
        }
        return false;
      });

      // Проверяем новые EmployeePlans
      const hasNewEmployeePlans = newEmployeePlans.length > 0;

      const serverShiftsPerPlan = resolveShiftsPerPlanValue(pointSystem?.shiftsPerPlan);
      const draftShiftsPerPlan = isValidShiftsPerPlanValue(shiftsPerPlanDraft)
        ? Number(normalizeShiftsPerPlanInput(shiftsPerPlanDraft))
        : null;
      const hasShiftsPerPlanChanges =
        draftShiftsPerPlan === null
          ? String(shiftsPerPlanDraft ?? '') !== String(serverShiftsPerPlan)
          : draftShiftsPerPlan !== serverShiftsPerPlan;

      const serverName = pointSystem?.name ? String(pointSystem.name) : '';
      const hasNameChanges = String(nameDraft ?? '').trim() !== serverName;
      return (
        hasContentCardDefaultChanges ||
        hasNewContentCardDefaults ||
        hasDampingChanges ||
        hasNewDampingCoefficients ||
        hasContentViewPlanChanges ||
        hasEmployeePlanChanges ||
        hasNewEmployeePlans ||
        hasShiftsPerPlanChanges ||
        hasNameChanges ||
        deletedContentCardDefaults.size > 0 ||
        deletedDampingCoefficients.size > 0 ||
        deletedEmployeePlans.size > 0
      );
    }, [
      contentCardDefaults,
      contentCardDefaultDraftsById,
      contentCardDefaultsServerById,
      deletedContentCardDefaults,
      newContentCardDefaults,
      dampingCoefficients,
      dampingDraftsById,
      deletedDampingCoefficients,
      newDampingCoefficients,
      contentViewPlanDraftsByKey,
      contentViewPlanServerByKey,
      contentViewPlanDirtyByKey,
      employeePlans,
      draftsById,
      deletedEmployeePlans,
      newEmployeePlans,
      pointSystem?.shiftsPerPlan,
      pointSystem?.name,
      shiftsPerPlanDraft,
      nameDraft,
    ]);

  const handleCloseConfirm = useCallback(() => {
      setShowCloseConfirmation(false);

      const { errors, idsToDelete } = validateContentCardDefaults();

      // Удаляем записи, где оба поля пустые
      idsToDelete.forEach((id) => {
        onContentCardDefaultDelete(id);
        // Удаляем ошибки для удаляемых записей
        delete errors[id];
      });

      // Валидация EmployeePlans
      const { errors: employeePlanErrors, idsToDelete: employeePlanIdsToDelete } = validateEmployeePlans();

      // Удаляем записи, где оба поля пустые
      employeePlanIdsToDelete.forEach((id) => {
        setDeletedEmployeePlans((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewEmployeePlans((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
        // Удаляем ошибки для удаляемых записей
        delete employeePlanErrors[id];
      });

      const employeePlanConflicts = validateEmployeePlanConflicts();
      setEmployeePlanConflictErrors(employeePlanConflicts);

      // Если есть ошибки валидации - показываем их и не закрываем модалку
      if (
        Object.keys(errors).length > 0 ||
        Object.keys(employeePlanErrors).length > 0 ||
        Object.keys(employeePlanConflicts).length > 0
      ) {
        setValidationErrors(errors);
        setEmployeePlanValidationErrors(employeePlanErrors);
        setShowValidationMessage(true);
        return;
      }

      // Если все ок - закрываем модалку
      setValidationErrors({});
      setEmployeePlanValidationErrors({});
      setEmployeePlanConflictErrors({});
      setShowValidationMessage(false);
      onClose();
    }, [validateContentCardDefaults, validateEmployeePlans, validateEmployeePlanConflicts, onContentCardDefaultDelete, onClose]);

    const handleCloseWithValidation = useCallback(() => {
      if (!canEditPointSystemSettings) {
        onClose();
        return;
      }

      // Проверяем наличие несохраненных изменений
      if (hasUnsavedChanges()) {
        // Показываем подтверждение
        setShowCloseConfirmation(true);
        return;
      }

      handleCloseConfirm();
    }, [canEditPointSystemSettings, hasUnsavedChanges, handleCloseConfirm, onClose]);

    const renderContentCardDefaults = useCallback(
      (items, type) => {
        if (!pointSystem) {
          return <div className={styles.emptyHint}>Сначала создайте балльную систему для доски</div>;
        }

        const hasHiddenVariants = Boolean(hasHiddenVariantsByType[type]);

        return (
          <div className={styles.contentCardDefaultsPane}>
            {hasHiddenVariants && (
              <div className={styles.variantsHeaderRow}>
                <Checkbox
                  toggle
                  className={styles.showHiddenToggle}
                  label="Показать скрытые начисления"
                  checked={showHiddenVariants}
                  onChange={(_, { checked }) => setShowHiddenVariants(Boolean(checked))}
                />
              </div>
            )}
            <div className={styles.contentCardDefaultsList}>
              {!items.length ? (
                <div className={styles.emptyHint}>Нет записей</div>
              ) : (
                items.map((item) => {
                  const itemId = String(item.id);
                  const hasNameError = validationErrors[itemId]?.name;
                  const hasValueError = validationErrors[itemId]?.value;
                  const hidden = isHiddenEntity(item);

                  return (
                    <Form
                      key={item.id}
                      className={classNames(styles.contentCardDefaultRow, {
                        [styles.hiddenEntityRow]: hidden,
                      })}
                    >
                      <Form.Field inline className={styles.contentCardDefaultFieldName}>
                        <label>Название</label>
                        <input
                          className={classNames({ [styles.fieldError]: hasNameError })}
                          placeholder="Введите название"
                          value={contentCardDefaultDraftsById[item.id]?.name ?? ''}
                          disabled={hidden}
                          onChange={(e) =>
                            handleContentCardDefaultDraftChange(item.id, 'name', e.target.value)
                          }
                        />
                      </Form.Field>
                      <Form.Field inline className={styles.contentCardDefaultFieldValue}>
                        <label>Баллы</label>
                        <input
                          type="number"
                          min="0"
                          className={classNames({ [styles.fieldError]: hasValueError })}
                          value={contentCardDefaultDraftsById[item.id]?.value ?? 0}
                          disabled={hidden}
                          onChange={(e) =>
                            handleContentCardDefaultDraftChange(item.id, 'value', e.target.value)
                          }
                        />
                      </Form.Field>
                      <Form.Field inline className={styles.contentCardDefaultFieldDescription}>
                        <label>Описание</label>
                        <input
                          placeholder="Введите текст"
                          value={contentCardDefaultDraftsById[item.id]?.description ?? ''}
                          disabled={hidden}
                          onChange={(e) =>
                            handleContentCardDefaultDraftChange(
                              item.id,
                              'description',
                              e.target.value,
                            )
                          }
                        />
                      </Form.Field>
                      {hidden ? (
                        <RestorePopup
                          title="Восстановить вариант"
                          content="Вариант снова станет доступен для выбора."
                          buttonContent="Восстановить"
                          onConfirm={() => handleContentCardDefaultRestore(item.id)}
                        >
                          <Button
                            type="button"
                            icon
                            basic
                            size="mini"
                            className={styles.contentCardDefaultRestoreButton}
                            title="Восстановить"
                          >
                            <Icon name="undo" />
                          </Button>
                        </RestorePopup>
                      ) : (
                        <DeletePopup
                          title="Скрыть вариант"
                          content="Вариант будет скрыт и недоступен для выбора. Исторические расчёты не изменятся."
                          buttonContent="Скрыть"
                          onConfirm={() => handleContentCardDefaultDelete(item.id)}
                        >
                          <Button
                            type="button"
                            icon
                            basic
                            size="mini"
                            className={styles.contentCardDefaultDeleteButton}
                            title="Скрыть"
                          >
                            <Icon name="trash" />
                          </Button>
                        </DeletePopup>
                      )}
                    </Form>
                  );
                })
              )}
            </div>

            <div className={styles.contentCardDefaultsActions}>
              <Button
                type="button"
                positive
                content="Добавить"
                onClick={() => handleContentCardDefaultCreate(type)}
              />
            </div>
          </div>
        );
      },
      [
        pointSystem,
        hasHiddenVariantsByType,
        showHiddenVariants,
        contentCardDefaultDraftsById,
        validationErrors,
        handleContentCardDefaultDraftChange,
        handleContentCardDefaultDelete,
        handleContentCardDefaultRestore,
        handleContentCardDefaultCreate,
        DeletePopup,
        RestorePopup,
      ],
    );

    const contentTypeIconOptions = useMemo(() => {
      const currentIcon = String(contentTypeFormIcon || '').trim();
      if (
        currentIcon &&
        !CONTENT_TYPE_ICON_OPTIONS.some((option) => option.value === currentIcon)
      ) {
        return [buildContentTypeIconOption(currentIcon, currentIcon, styles.contentTypeIconOption), ...CONTENT_TYPE_ICON_OPTIONS];
      }
      return CONTENT_TYPE_ICON_OPTIONS;
    }, [contentTypeFormIcon]);

    const selectedContentTypeIconTitle = useMemo(
      () => getContentTypeIconTitle(contentTypeFormIcon),
      [contentTypeFormIcon],
    );

    const openCreateContentTypeForm = useCallback(() => {
      setContentTypeFormMode('create');
      setContentTypeFormId(null);
      setContentTypeFormTitle('');
      setContentTypeFormIcon('');
      setContentTypeFormIsHidden(false);
      setContentTypeFormOpen(true);
    }, []);

    const openEditContentTypeForm = useCallback((contentType) => {
      setContentTypeFormMode('edit');
      setContentTypeFormId(contentType.id);
      setContentTypeFormTitle(contentType.title || '');
      setContentTypeFormIcon(contentType.icon || '');
      setContentTypeFormIsHidden(isHiddenEntity(contentType));
      setContentTypeFormOpen(true);
    }, []);

    const closeContentTypeForm = useCallback(() => {
      setContentTypeFormOpen(false);
      setContentTypeFormMode('create');
      setContentTypeFormId(null);
      setContentTypeFormTitle('');
      setContentTypeFormIcon('');
      setContentTypeFormIsHidden(false);
    }, []);

    const handleContentTypeFormSubmit = useCallback(() => {
      const title = String(contentTypeFormTitle || '').trim();
      if (!title) {
        return;
      }

      const icon = String(contentTypeFormIcon || '').trim() || null;

      if (contentTypeFormMode === 'edit' && contentTypeFormId) {
        onContentTypeUpdate(contentTypeFormId, { title, icon });
      } else {
        onContentTypeCreate({ title, icon });
        setContentTypeTabIndex(visibleContentTypes.length);
      }

      closeContentTypeForm();
    }, [
      contentTypeFormTitle,
      contentTypeFormIcon,
      contentTypeFormMode,
      contentTypeFormId,
      onContentTypeUpdate,
      onContentTypeCreate,
      visibleContentTypes,
      closeContentTypeForm,
    ]);

    const handleContentTypeFormDelete = useCallback(() => {
      if (!contentTypeFormId) {
        return;
      }
      onContentTypeDelete(contentTypeFormId);
      closeContentTypeForm();
    }, [contentTypeFormId, onContentTypeDelete, closeContentTypeForm]);

    const handleContentTypeFormRestore = useCallback(() => {
      if (!contentTypeFormId) {
        return;
      }
      onContentTypeUpdate(contentTypeFormId, { isHidden: false });
      setContentTypeFormIsHidden(false);
    }, [contentTypeFormId, onContentTypeUpdate]);

    const contentCardDefaultsPanes = useMemo(() => {
      const typePanes = visibleContentTypes.map((contentType) => {
        const hidden = isHiddenEntity(contentType);
        return {
          menuItem: (
            <Menu.Item
              key={contentType.id}
              className={classNames(styles.contentTypeTabItem, {
                [styles.contentTypeTabItemHidden]: hidden,
              })}
            >
              <span className={styles.contentTypeTabLabel}>
                {contentType.title || contentType.key}
                {hidden ? ' (скрыт)' : ''}
              </span>
              {canEditPointSystemSettings && (
                <button
                  type="button"
                  className={styles.contentTypeTabEdit}
                  title="Редактировать"
                  aria-label="Редактировать"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    openEditContentTypeForm(contentType);
                  }}
                >
                  <Icon name="pencil" />
                </button>
              )}
            </Menu.Item>
          ),
          render: () => (
            <Tab.Pane>
              {renderContentCardDefaults(
                contentCardDefaultsByType[contentType.key] || [],
                contentType.key,
              )}
            </Tab.Pane>
          ),
        };
      });

      if (!canEditPointSystemSettings) {
        return typePanes;
      }

      return [
        ...typePanes,
        {
          menuItem: (
            <Menu.Item
              key="__add_content_type__"
              className={styles.addContentTypeTabItem}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openCreateContentTypeForm();
              }}
            >
              Добавить
            </Menu.Item>
          ),
          render: () => null,
        },
      ];
    }, [
      visibleContentTypes,
      contentCardDefaultsByType,
      renderContentCardDefaults,
      canEditPointSystemSettings,
      openEditContentTypeForm,
      openCreateContentTypeForm,
    ]);

    const handleContentTypeTabChange = useCallback(
      (e, data) => {
        const nextIndex = data.activeIndex;
        const typesCount = visibleContentTypes.length;

        // Последний "таб" — кнопка добавления, индекс не меняем
        if (canEditPointSystemSettings && nextIndex >= typesCount) {
          openCreateContentTypeForm();
          return;
        }

        setContentTypeTabIndex(nextIndex);
      },
      [visibleContentTypes, canEditPointSystemSettings, openCreateContentTypeForm],
    );

    // Функция сохранения всех изменений
    const handleSave = useCallback(async () => {
      if (!canEditPointSystemSettings || !pointSystemId) {
        return false;
      }

      const draftsForSave = pendingDampingDraftsOverrideRef.current || dampingDraftsById;

      // Валидация ContentCardDefaults
      const { errors, idsToDelete } = validateContentCardDefaults();

      // Добавляем пустые записи в список удаленных
      idsToDelete.forEach((id) => {
        setDeletedContentCardDefaults((prev) => new Set([...prev, String(id)]));
      });

      // Валидация EmployeePlans
      const { errors: employeePlanErrors, idsToDelete: employeePlanIdsToDelete } = validateEmployeePlans();

      // Добавляем пустые записи в список удаленных
      employeePlanIdsToDelete.forEach((id) => {
        setDeletedEmployeePlans((prev) => new Set([...prev, String(id)]));
        // Удаляем из drafts
        setDraftsById((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        // Удаляем из новых, если это новый элемент
        setNewEmployeePlans((prev) => prev.filter((item) => String(item.tempId) !== String(id)));
      });

      setEmployeePlanValidationErrors(employeePlanErrors);

      // Проверка конфликтов диапазонов планов сотрудников
      const employeePlanConflicts = validateEmployeePlanConflicts();
      setEmployeePlanConflictErrors(employeePlanConflicts);

      const isShiftsPerPlanValid = isValidShiftsPerPlanValue(shiftsPerPlanDraft);
      setShiftsPerPlanError(!isShiftsPerPlanValid);

      const nextName = String(nameDraft || '').trim();
      const isNameValid = nextName.length > 0;
      setNameError(!isNameValid);

      // Валидация DampingCoefficients
      const dampingErrors = validateDampingCoefficients(draftsForSave);
      setDampingValidationErrors(dampingErrors);
      
      // Проверка конфликтов диапазонов
      const conflicts = validateDampingCoefficientConflicts(draftsForSave);
      setDampingConflictErrors(conflicts);

      // Если есть ошибки валидации или конфликты - показываем их и не сохраняем
      if (
        Object.keys(errors).length > 0 ||
        Object.keys(employeePlanErrors).length > 0 ||
        Object.keys(employeePlanConflicts).length > 0 ||
        Object.keys(dampingErrors).length > 0 ||
        Object.keys(conflicts).length > 0 ||
        !isShiftsPerPlanValid ||
        !isNameValid
      ) {
        setValidationErrors(errors);
        setShowValidationMessage(true);

        // Если есть пустые "до" — подсвечиваем и переводим фокус на первое такое поле
        if (Object.keys(dampingErrors).length > 0) {
          const orderedIds = [
            ...dampingCoefficients
              .filter((dc) => !deletedDampingCoefficients.has(String(dc.id)))
              .map((dc) => String(dc.id)),
            ...newDampingCoefficients.map((ni) => String(ni.tempId)),
          ];

          const firstMissingToId = orderedIds.find((id) => {
            const draft = draftsForSave[id];
            return draft && !draft.isInfinity && (draft.to === null || draft.to === '' || typeof draft.to === 'undefined');
          });

          if (firstMissingToId) {
            requestAnimationFrame(() => {
              const el = dampingToInputRefs.current[firstMissingToId];
              if (!el) {
                return;
              }
              try {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              } catch (e) {
                // ignore
              }
              if (typeof el.focus === 'function') {
                el.focus();
              }
              if (typeof el.select === 'function') {
                el.select();
              }
            });
          }
        }

        return false;
      }

      // Если в диапазонах есть "зазоры" (непокрытые области) — предлагаем автоматически скорректировать значения "от".
      if (skipDampingGapsCheckRef.current) {
        skipDampingGapsCheckRef.current = false;
      } else {
        const { hasGaps, fixedDraftsById } = buildDampingGapsFixIfNeeded(draftsForSave);
        if (hasGaps && fixedDraftsById) {
          pendingDampingGapsFixedDraftsRef.current = fixedDraftsById;
          setShowDampingGapsConfirmation(true);
          return false;
        }
      }

      try {
        // Удаляем ContentCardDefaults
        deletedContentCardDefaults.forEach((id) => {
          // Проверяем, что это не новый элемент
          if (!id.startsWith('new-')) {
            onContentCardDefaultDelete(id);
            setContentCardDefaultsServerById((prev) => {
              if (!prev[id]) {
                return prev;
              }
              const next = { ...prev };
              delete next[id];
              return next;
            });
          }
        });

        // Обновляем существующие ContentCardDefaults
        contentCardDefaults.forEach((item) => {
          const id = String(item.id);
          // Пропускаем удаленные и скрытые
          if (deletedContentCardDefaults.has(id) || isHiddenEntity(item)) {
            return;
          }
          const draft = contentCardDefaultDraftsById[id];
          if (draft) {
            // Проверяем, есть ли изменения
            const hasChanges =
              draft.name !== (item.name ?? '') ||
              draft.value !== (item.value ?? 0) ||
              draft.description !== (item.description ?? '');
            if (hasChanges) {
              onContentCardDefaultUpdate(id, {
                name: draft.name,
                value: Number(draft.value) || 0,
                description: draft.description,
              });
              // Optimistically update server snapshot to avoid "unsaved changes" prompt until store updates
              setContentCardDefaultsServerById((prev) => ({
                ...prev,
                [id]: {
                  name: draft.name ?? '',
                  value: Number(draft.value) || 0,
                  description: draft.description ?? '',
                },
              }));
            }
          }
        });

        // Создаем новые ContentCardDefaults
        newContentCardDefaults.forEach((newItem) => {
          const draft = contentCardDefaultDraftsById[newItem.tempId];
          if (draft && draft.name && draft.value) {
            onContentCardDefaultCreate(newItem.type, {
              name: draft.name,
              value: Number(draft.value) || 0,
              description: draft.description || '',
            });
          }
        });

        // Удаляем DampingCoefficients
        deletedDampingCoefficients.forEach((id) => {
          // Проверяем, что это не новый элемент
          if (!id.startsWith('new-')) {
            onDampingCoefficientDelete(id);
          }
        });

        // Обновляем существующие DampingCoefficients
        dampingCoefficients.forEach((dc) => {
          const id = String(dc.id);
          // Пропускаем удаленные
          if (deletedDampingCoefficients.has(id)) {
            return;
          }
          const draft = draftsForSave[id];
          if (draft) {
            // Преобразуем значения для корректного сравнения
            const draftFrom = draft.isFromInfinity 
              ? INFINITY_VALUE 
              : (draft.from !== null && draft.from !== '' ? Number(draft.from) : null);
            // Если бесконечность, преобразуем null в INFINITY_VALUE
            const draftTo = draft.isInfinity 
              ? INFINITY_VALUE 
              : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
            const draftValue = Number(draft.value) || 1;
            const originalFrom = isFromInfinity(dc.from) ? INFINITY_VALUE : (dc.from !== null && dc.from !== '' ? Number(dc.from) : null);
            const originalTo = isInfinity(dc.to) ? INFINITY_VALUE : (dc.to !== null && dc.to !== '' ? Number(dc.to) : null);
            const originalValue = Number(dc.value) || 1;
            
            // Проверяем, есть ли изменения
            const hasChanges =
              draftFrom !== originalFrom ||
              draftTo !== originalTo ||
              draftValue !== originalValue;
            if (hasChanges) {
              onDampingCoefficientUpdate(id, {
                from: draftFrom,
                to: draftTo,
                value: draftValue,
              });
            }
          }
        });

        // Создаем новые DampingCoefficients
        newDampingCoefficients.forEach((newItem) => {
          const draft = draftsForSave[newItem.tempId];
          if (draft) {
            // Если бесконечность, преобразуем null в INFINITY_VALUE
            const fromValue = draft.isFromInfinity 
              ? INFINITY_VALUE 
              : (draft.from !== null && draft.from !== '' ? Number(draft.from) : null);
            const toValue = draft.isInfinity 
              ? INFINITY_VALUE 
              : (draft.to !== null && draft.to !== '' ? Number(draft.to) : null);
            onDampingCoefficientCreate(pointSystemId, {
              from: fromValue,
              to: toValue,
              value: Number(draft.value) || 1,
            });
          }
        });

        // Сохраняем только изменённые ContentViewPlan (без автосоздания при клике по месяцам)
        Object.keys(contentViewPlanDirtyByKey).forEach((key) => {
          if (!contentViewPlanDirtyByKey[key]) {
            return;
          }

          const [yearRaw, monthRaw] = String(key).split('-');
          const year = Number(yearRaw);
          const month = Number(monthRaw);
          if (!Number.isFinite(year) || !Number.isFinite(month)) {
            return;
          }

          const draft = contentViewPlanDraftsByKey[key] || {};
          const draftValues = draft.values || {};
          const server = contentViewPlanServerByKey[key];
          const serverValues = (server && server.values) || {};
          const keys = new Set([
            ...contentTypeKeys,
            ...Object.keys(draftValues),
            ...Object.keys(serverValues),
          ]);
          const nextValues = {};
          keys.forEach((typeKey) => {
            nextValues[typeKey] = Number(draftValues[typeKey] || 0);
          });

          if (server && server.id) {
            const hasChanges = Array.from(keys).some(
              (typeKey) => Number(draftValues[typeKey] || 0) !== Number(serverValues[typeKey] || 0),
            );

            if (hasChanges) {
              onContentViewPlanUpdate(server.id, {
                values: nextValues,
              });
            }
          } else {
            onContentViewPlanGetOrCreate(year, month, pointSystemId, nextValues);
          }

          setContentViewPlanServerByKey((prev) => ({
            ...prev,
            [key]: {
              ...(prev[key] || {}),
              id: (server && server.id) || prev[key]?.id || null,
              values: nextValues,
            },
          }));
          setContentViewPlanDirtyByKey((prev) => ({ ...prev, [key]: false }));
        });

        // Удаляем EmployeePlans
        deletedEmployeePlans.forEach((id) => {
          // Проверяем, что это не новый элемент
          if (!id.startsWith('new-')) {
            onEmployeePlanDelete(id);
          }
        });

        const nextShiftsPerPlan = Number(normalizeShiftsPerPlanInput(shiftsPerPlanDraft));
        // Сохранение шаблона всегда пушит настройки в linked board PS
        onPointSystemUpdate({
          name: nextName,
          shiftsPerPlan: nextShiftsPerPlan,
          syncLinked: true,
        });

        // Обновляем существующие EmployeePlans
        employeePlans.forEach((plan) => {
          const id = String(plan.id);
          // Пропускаем удаленные
          if (deletedEmployeePlans.has(id)) {
            return;
          }
          const draft = draftsById[id];
          if (draft) {
            const draftFrom = Math.trunc(Number(draft.from)) || 0;
            const draftTo = Math.trunc(Number(draft.to)) || 0;
            const hasChanges =
              draftFrom !== (plan.from ?? 0) || draftTo !== (plan.to ?? 0);
            if (hasChanges) {
              onEmployeePlanUpdate(id, {
                from: draftFrom,
                to: draftTo,
              });
            }
          }
        });

        // Создаем новые EmployeePlans
        newEmployeePlans.forEach((newItem) => {
          const draft = draftsById[newItem.tempId];
          if (draft) {
            onEmployeePlanCreate({
              from: Math.trunc(Number(draft.from)) || 0,
              to: Math.trunc(Number(draft.to)) || 0,
              sort: newItem.sort,
            });
          }
        });

        // Очищаем состояния после успешного сохранения
        setDeletedContentCardDefaults(new Set());
        setDeletedDampingCoefficients(new Set());
        setDeletedEmployeePlans(new Set());
        setNewContentCardDefaults([]);
        setNewDampingCoefficients([]);
        setNewEmployeePlans([]);
        setValidationErrors({});
        setDampingValidationErrors({});
        setEmployeePlanValidationErrors({});
        setEmployeePlanConflictErrors({});
        setShowValidationMessage(false);
        pendingDampingDraftsOverrideRef.current = null;
        // Не очищаем contentViewPlanDraftsByKey/contentViewPlanServerByKey здесь,
        // иначе на слабых/асинхронных обновлениях стора значения могут "сброситься" до 0.
        onContentViewPlansFetch(pointSystemId);

        // Показываем сообщение об успехе
        setSaveMessage({ type: 'success', text: 'Данные успешно сохранены' });
        
        // Автоматически скрываем сообщение через 3 секунды
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);

        return true;
      } catch (error) {
        console.error('Ошибка при сохранении:', error);
        
        // Показываем сообщение об ошибке
        const errorText = error?.message || 'Произошла ошибка при сохранении данных';
        setSaveMessage({ type: 'error', text: errorText });
        
        // Автоматически скрываем сообщение через 5 секунд
        setTimeout(() => {
          setSaveMessage(null);
        }, 5000);

        return false;
      }
    }, [
      canEditPointSystemSettings,
      pointSystemId,
      validateContentCardDefaults,
      validateEmployeePlans,
      validateEmployeePlanConflicts,
      validateDampingCoefficients,
      buildDampingGapsFixIfNeeded,
      deletedContentCardDefaults,
      deletedDampingCoefficients,
      deletedEmployeePlans,
      newContentCardDefaults,
      newDampingCoefficients,
      newEmployeePlans,
      contentCardDefaults,
      contentCardDefaultDraftsById,
      dampingCoefficients,
      dampingDraftsById,
      contentViewPlanDraftsByKey,
      contentViewPlanServerByKey,
      contentViewPlanDirtyByKey,
      contentTypeKeys,
      employeePlans,
      draftsById,
      onContentCardDefaultDelete,
      onContentCardDefaultUpdate,
      onContentCardDefaultCreate,
      onDampingCoefficientDelete,
      onDampingCoefficientUpdate,
      onDampingCoefficientCreate,
      validateDampingCoefficientConflicts,
      onContentViewPlanUpdate,
      onContentViewPlanGetOrCreate,
      onContentViewPlansFetch,
      onEmployeePlanUpdate,
      onEmployeePlanDelete,
      onEmployeePlanCreate,
      onPointSystemUpdate,
      pointSystem?.shiftsPerPlan,
      pointSystem?.name,
      shiftsPerPlanDraft,
      nameDraft,
    ]);

    const handleDampingGapsFixAndSave = useCallback(() => {
      const fixedDraftsById = pendingDampingGapsFixedDraftsRef.current;
      pendingDampingGapsFixedDraftsRef.current = null;
      setShowDampingGapsConfirmation(false);

      if (!fixedDraftsById) {
        return;
      }

      pendingDampingDraftsOverrideRef.current = fixedDraftsById;
      setDampingDraftsById(fixedDraftsById);

      // Сразу показываем актуальные конфликты (если вдруг появились из-за ручных правок пользователя)
      const conflicts = validateDampingCoefficientConflicts(fixedDraftsById);
      setDampingConflictErrors(conflicts);

      // Чтобы не зациклиться на повторном подтверждении
      skipDampingGapsCheckRef.current = true;

      requestAnimationFrame(() => {
        handleSave();
      });
    }, [handleSave, validateDampingCoefficientConflicts]);

    const handleDampingGapsSaveAsIs = useCallback(() => {
      pendingDampingGapsFixedDraftsRef.current = null;
      setShowDampingGapsConfirmation(false);

      // Продолжаем сохранение без автокоррекции и без повторного подтверждения.
      skipDampingGapsCheckRef.current = true;
      pendingDampingDraftsOverrideRef.current = null;

      requestAnimationFrame(() => {
        handleSave();
      });
    }, [handleSave]);

    const handleApplyToBoardConfirmAndSave = useCallback(async () => {
      if (!selectedTemplateId) {
        setShowApplyToBoardConfirmation(false);
        return;
      }

      const saved = await handleSave();
      if (saved === false) {
        setShowApplyToBoardConfirmation(false);
        return;
      }

      onContentTypeTemplateApply(selectedTemplateId);
      setShowApplyToBoardConfirmation(false);
      setSaveMessage({
        type: 'success',
        text: isTemplateSwitch
          ? `Шаблон сменён для доски «${currentBoardName}»`
          : `Шаблон применён к доске «${currentBoardName}»`,
      });
    }, [
      selectedTemplateId,
      handleSave,
      onContentTypeTemplateApply,
      currentBoardName,
      isTemplateSwitch,
    ]);

    return (
      <>
        <Modal
          open={open && !showCloseConfirmation}
          closeIcon
          size="large"
          centered={false}
          className={styles.modal}
          closeOnDimmerClick={false}
          onClose={handleCloseWithValidation}
        >
          <Modal.Header>Шаблоны балльной системы</Modal.Header>
          <Modal.Content className={styles.content}>
            <div className={styles.layout}>
              <div className={styles.sidebar}>
                <div className={styles.sidebarTitleRow}>
                  <div className={styles.sidebarTitle}>Шаблоны</div>
                  {canEditPointSystemSettings && (
                    <Checkbox
                      toggle
                      className={styles.showHiddenToggle}
                      label="Показать скрытые"
                      checked={showHiddenTemplates}
                      onChange={(_, { checked }) => setShowHiddenTemplates(Boolean(checked))}
                    />
                  )}
                </div>
                <div className={styles.templateList}>
                  {visibleTemplates.length === 0 && (
                    <div className={styles.sidebarEmpty}>Нет шаблонов</div>
                  )}
                  {visibleTemplates.map((template) => {
                    const isSelected = String(template.id) === String(selectedTemplateId || '');
                    const hidden = isHiddenEntity(template);
                    return (
                      <div
                        key={template.id}
                        className={classNames(styles.templateRow, {
                          [styles.hiddenRow]: hidden,
                        })}
                      >
                        <button
                          type="button"
                          className={classNames(styles.templateButton, {
                            [styles.templateButtonActive]: isSelected,
                          })}
                          onClick={() => {
                            if (hidden) {
                              return;
                            }
                            handleSelectTemplate(template.id);
                          }}
                        >
                          <span className={styles.templateName}>{template.name}</span>
                          {template.isDefault && (
                            <span className={styles.defaultBadge}>default</span>
                          )}
                          {hidden && <span className={styles.hiddenBadge}>скрыт</span>}
                        </button>
                        {canEditPointSystemSettings &&
                          !template.isDefault &&
                          (hidden ? (
                            <button
                              type="button"
                              className={styles.sidebarRestoreButton}
                              title="Восстановить шаблон"
                              aria-label="Восстановить шаблон"
                              onClick={(event) => handleTemplateRestore(template.id, event)}
                            >
                              <Icon name="undo" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={styles.sidebarDeleteButton}
                              title="Скрыть шаблон"
                              aria-label="Скрыть шаблон"
                              onClick={(event) => handleTemplateDeleteRequest(template.id, event)}
                            >
                              <Icon name="trash" />
                            </button>
                          ))}
                      </div>
                    );
                  })}
                </div>
                {canEditPointSystemSettings && (
                  <Button
                    type="button"
                    positive
                    content="Создать"
                    fluid
                    onClick={handleStartCreateTemplate}
                  />
                )}
              </div>

              <div className={styles.main}>
                {!selectedTemplateId || !pointSystem ? (
                  <div className={styles.emptyMain}>
                    Выберите шаблон слева или нажмите «Создать»
                  </div>
                ) : (
                  <div ref={modalContentRef}>
      {!canEditPointSystemSettings && (
        <Message warning className={styles.readOnlyNotice}>
          У вас нет прав для изменения настроек бальной системы
        </Message>
      )}
      {showValidationMessage && (
        <Message ref={validationMessageRef} negative style={{ margin: 0, borderRadius: 0 }}>
          <Message.Header>Ошибка</Message.Header>
          <p>
            {nameError && 'Укажите название шаблона балльной системы'}
            {nameError && (Object.keys(validationErrors).length > 0 || Object.keys(employeePlanValidationErrors).length > 0 || Object.keys(employeePlanConflictErrors).length > 0 || shiftsPerPlanError || Object.keys(dampingValidationErrors).length > 0 || Object.keys(dampingConflictErrors).length > 0) && '. '}
            {Object.keys(validationErrors).length > 0 && 'В секции ТИП КОНТЕНТА есть незаполненные обязательные поля'}
            {Object.keys(validationErrors).length > 0 && (Object.keys(employeePlanValidationErrors).length > 0 || Object.keys(employeePlanConflictErrors).length > 0 || shiftsPerPlanError || Object.keys(dampingValidationErrors).length > 0 || Object.keys(dampingConflictErrors).length > 0) && '. '}
            {Object.keys(employeePlanValidationErrors).length > 0 &&
              (() => {
                const hasFromRequired = Object.values(employeePlanValidationErrors).some(
                  (e) => e && e.from && !e.to,
                );
                const hasToRequired = Object.values(employeePlanValidationErrors).some(
                  (e) => e && e.to && !e.from,
                );
                const hasRangeError = Object.values(employeePlanValidationErrors).some(
                  (e) => e && e.to && e.from,
                );

                const parts = [];
                if (hasFromRequired) {
                  parts.push('В секции ПЛАНЫ СОТРУДНИКОВ поле "От" обязательно для заполнения');
                }
                if (hasToRequired) {
                  parts.push('В секции ПЛАНЫ СОТРУДНИКОВ поле "До" обязательно для заполнения');
                }
                if (hasRangeError && !hasFromRequired && !hasToRequired) {
                  parts.push('В секции ПЛАНЫ СОТРУДНИКОВ значение "От" не может быть больше значения "До"');
                }

                return parts.join('. ');
              })()}
            {Object.keys(employeePlanValidationErrors).length > 0 && (Object.keys(employeePlanConflictErrors).length > 0 || shiftsPerPlanError) && '. '}
            {Object.keys(employeePlanConflictErrors).length > 0 && 'В секции ПЛАНЫ СОТРУДНИКОВ есть конфликты диапазонов'}
            {Object.keys(employeePlanConflictErrors).length > 0 && shiftsPerPlanError && '. '}
            {shiftsPerPlanError && 'В секции ПЛАНЫ СОТРУДНИКОВ поле "Отработанных смен" обязательно и должно быть больше 0'}
            {(Object.keys(employeePlanValidationErrors).length > 0 || Object.keys(employeePlanConflictErrors).length > 0 || shiftsPerPlanError) && (Object.keys(dampingValidationErrors).length > 0 || Object.keys(dampingConflictErrors).length > 0) && '. '}
            {Object.keys(dampingValidationErrors).length > 0 &&
              (() => {
                const hasToRequired = Object.values(dampingValidationErrors).some(
                  (e) => e && e.to && !e.from,
                );
                const hasRangeError = Object.values(dampingValidationErrors).some(
                  (e) => e && e.to && e.from,
                );

                const parts = [];
                if (hasToRequired) {
                  parts.push('В секции НАСТРОЙКА ДЕМПФИРУЮЩЕГО КОЭФФИЦИЕНТА поле "до" обязательно для заполнения');
                }
                if (hasRangeError) {
                  parts.push('В секции НАСТРОЙКА ДЕМПФИРУЮЩЕГО КОЭФФИЦИЕНТА значение "от" не может быть больше значения "до"');
                }

                return parts.join('. ');
              })()}
            {Object.keys(dampingValidationErrors).length > 0 && Object.keys(dampingConflictErrors).length > 0 && '. '}
            {Object.keys(dampingConflictErrors).length > 0 && 'В секции НАСТРОЙКА ДЕМПФИРУЮЩЕГО КОЭФФИЦИЕНТА есть конфликты диапазонов'}
          </p>
        </Message>
      )}
      <div
        className={classNames(styles.sections, {
          [styles.readOnlySections]: !canEditPointSystemSettings,
        })}
      >
        <div className={styles.section}>
          <div className={styles.sectionBody}>
            <div className={styles.appliedBoardsSection}>
              <div className={styles.appliedBoardsTitle}>
                Шаблон применен к следующим доскам
              </div>
              {boardsUsingTemplate.active.length === 0 &&
              boardsUsingTemplate.archived.length === 0 ? (
                <div className={styles.appliedBoardsEmpty}>Пока не применён ни к одной доске</div>
              ) : (
                <>
                  <div className={styles.appliedBoardsGroup}>
                    <div className={styles.appliedBoardsGroupTitle}>Активные</div>
                    {boardsUsingTemplate.active.length > 0 ? (
                      <div className={styles.appliedBoardsBadges}>
                        {boardsUsingTemplate.active.map((item) => (
                          <span
                            key={item.boardId}
                            className={classNames(
                              styles.appliedBoardBadge,
                              styles.appliedBoardBadgeActive,
                            )}
                          >
                            {item.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.appliedBoardsEmpty}>Нет активных досок</div>
                    )}
                  </div>
                  <div className={styles.appliedBoardsGroup}>
                    <div className={styles.appliedBoardsGroupTitle}>Архивные</div>
                    {boardsUsingTemplate.archived.length > 0 ? (
                      <div className={styles.appliedBoardsBadges}>
                        {boardsUsingTemplate.archived.map((item) => (
                          <span
                            key={item.boardId}
                            className={classNames(
                              styles.appliedBoardBadge,
                              styles.appliedBoardBadgeArchived,
                            )}
                          >
                            {item.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.appliedBoardsEmpty}>Нет архивных досок</div>
                    )}
                  </div>
                </>
              )}
            </div>
            <Form className={styles.pointSystemNameRow}>
              <Form.Field className={styles.pointSystemNameField}>
                <label>Название шаблона балльной системы</label>
                <input
                  type="text"
                  maxLength={128}
                  className={classNames({ [styles.fieldError]: nameError })}
                  value={nameDraft}
                  onChange={handleNameChange}
                  disabled={!canEditPointSystemSettings}
                  placeholder="Введите название"
                />
              </Form.Field>
            </Form>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>Планы сотрудников</div>
              <SectionInfo
                title="Планы сотрудников"
                content="Планы сотрудников используются для отчётности и определения уровня сотрудника. Диапазоны «От»/«До» задаются по общему количеству баллов за указанное число отработанных смен. Поле «Отработанных смен» задаёт калибровку этих диапазонов."
              />
            </div>
          </div>
          <div className={styles.sectionBody}>
            {!pointSystem ? (
              <div className={styles.emptyHint}>Сначала создайте балльную систему для доски</div>
            ) : (
              <>
                <Form className={styles.shiftsPerPlanRow}>
                  <Form.Field inline className={styles.employeePlanField}>
                    <label>Отработанных смен</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className={classNames({ [styles.fieldError]: shiftsPerPlanError })}
                      value={shiftsPerPlanDraft}
                      onChange={handleShiftsPerPlanChange}
                      disabled={!canEditPointSystemSettings}
                    />
                  </Form.Field>
                </Form>

                <div className={styles.employeePlansList}>
                  {sortedEmployeePlans.map((plan, index) => {
                    const planId = String(plan.id);
                    const hasConflictError = employeePlanConflictErrors[planId];
                    const hasFromError = employeePlanValidationErrors[planId]?.from || hasConflictError;
                    const hasToError = employeePlanValidationErrors[planId]?.to || hasConflictError;

                    return (
                      <Form key={plan.id} className={styles.employeePlanRow}>
                        <div className={styles.employeePlanLevel}>{`${index + 1} уровень`}</div>

                        <Form.Field inline className={styles.employeePlanField}>
                          <label>От</label>
                          <input
                            type="number"
                            step="1"
                            className={classNames({ [styles.fieldError]: hasFromError })}
                            value={draftsById[plan.id]?.from ?? 0}
                            onChange={(e) => handleDraftChange(plan.id, 'from', e.target.value)}
                          />
                        </Form.Field>

                        <Form.Field inline className={styles.employeePlanField}>
                          <label>До</label>
                          <input
                            type="number"
                            step="1"
                            className={classNames({ [styles.fieldError]: hasToError })}
                            value={draftsById[plan.id]?.to ?? 0}
                            onChange={(e) => handleDraftChange(plan.id, 'to', e.target.value)}
                          />
                        </Form.Field>

                      <DeletePopup
                        title="Удаление"
                        content="Удалить элемент?"
                        buttonContent="Удалить"
                        onConfirm={() => handlePlanDelete(plan.id)}
                      >
                        <Button
                          type="button"
                          icon
                          basic
                          color="red"
                          size="mini"
                          className={styles.employeePlanDeleteButton}
                        >
                          <Icon name="trash" />
                        </Button>
                      </DeletePopup>
                      {hasConflictError && (
                        <div className={styles.conflictError}>
                          Конфликт с другими значениями
                        </div>
                      )}
                    </Form>
                    );
                  })}
                </div>

                <div className={styles.employeePlansActions}>
                  <Button positive content="Добавить" onClick={handlePlanAdd} />
                </div>
              </>
            )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>Планы для ключевых показателей работы</div>
              <SectionInfo
                title="Планы для ключевых показателей работы"
                content="Это базовое значение просмотров (viewsBase) для выбранного месяца. Оно используется при расчёте демпфирующего коэффициента как точка отсчёта для % отклонения."
              />
            </div>
          </div>
          <div className={styles.sectionBody}>
            <div className={styles.contentViewPlanBlock}>
              <div className={styles.contentViewPlanBlockTitle}>Календарь</div>
              <Form className={styles.readOnlyNavigation}>
                <Form.Select
                  label="Год"
                  options={contentViewPlanYearOptions}
                  value={contentViewPlanYear}
                  onChange={handleContentViewPlanYearChange}
                />
              </Form>
            </div>

            <div className={styles.contentViewPlanBlock}>
              <div className={styles.contentViewPlanBlockTitle}>Месяц</div>
              <div className={classNames(styles.monthButtonsGrid, styles.readOnlyNavigation)}>
                {contentViewPlanMonthTitles.map((title, idx) => {
                  const month = idx + 1;
                  const isActive = contentViewPlanMonth === month;
                  const isFilled = filledContentViewPlanMonths.has(month);

                  return (
                    <Button
                      // eslint-disable-next-line react/no-array-index-key
                      key={title}
                      type="button"
                      size="small"
                      basic={!isActive}
                      primary={isActive}
                      className={classNames(styles.monthButton, {
                        [styles.monthButtonFilled]: isFilled && !isActive,
                      })}
                      onClick={() => handleContentViewPlanMonthClick(month)}
                    >
                      {title}
                    </Button>
                  );
                })}
              </div>

              <div className={classNames(styles.contentViewPlanBlockTitle, styles.contentViewPlanFieldsTitle)}>
                Опорные значения для расчета коэффициента
              </div>

              <div className={styles.contentViewPlanFields}>
                {visibleContentTypes.length === 0 ? (
                  !hasPointSystemIdentity ? (
                    <div className={styles.emptyHint}>
                      Выберите шаблон типов контента или добавьте типы вручную
                    </div>
                  ) : null
                ) : (
                  <Form>
                    <div className={styles.contentViewPlanFieldsGrid}>
                      {visibleContentTypes.map((contentType) => (
                        <Form.Field
                          inline
                          key={contentType.id || contentType.key}
                          className={styles.contentViewPlanField}
                        >
                          <label className={styles.contentViewPlanFieldLabel}>
                            <span className={styles.contentViewPlanFieldLabelText}>
                              {contentType.title || contentType.key}
                            </span>
                            {canManageContentTypesInViewPlan && (
                              <button
                                type="button"
                                className={styles.contentViewPlanFieldEdit}
                                title="Редактировать"
                                aria-label="Редактировать"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openEditContentTypeForm(contentType);
                                }}
                              >
                                <Icon name="pencil" />
                              </button>
                            )}
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={(contentViewPlanForm.values || {})[contentType.key] ?? 0}
                            onChange={(e) =>
                              handleContentViewPlanFieldChange(contentType.key, e.target.value)
                            }
                          />
                        </Form.Field>
                      ))}
                    </div>
                  </Form>
                )}
                {canManageContentTypesInViewPlan && hasPointSystemIdentity && (
                  <div className={styles.contentViewPlanAddTypeActions}>
                    <Button
                      type="button"
                      positive
                      content="Добавить"
                      className={styles.contentViewPlanAddType}
                      onClick={openCreateContentTypeForm}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>Настройка демпфирующего коэффициента</div>
              <SectionInfo
                title="Как заполнить коэффициенты"
                content="Укажите, на сколько процентов просмотры отличаются от плана. 0% — план выполнен; меньше 0% — ниже плана; больше 0% — выше плана. Например: план 300, просмотров 600 — отклонение +100%. Для каждого диапазона укажите коэффициент: 1 — без изменения; 0,8 — на 20% меньше; 1,2 — на 20% больше. Коэффициент применяется к баллам задачи"
              />
            </div>
          </div>
          <div className={styles.sectionBody}>
            {(visibleDampingCoefficients.length > 0 || newDampingCoefficients.length > 0) && (
              <div className={styles.dampingBlock}>
                {/* Существующие элементы (исключая удаленные) */}
                {visibleDampingCoefficients.map((dc, index) => {
                    const id = String(dc.id);
                    const isFirst = index === 0;
                    const draft = dampingDraftsById[id] || {
                      from: isFromInfinity(dc.from) ? null : (dc.from ?? null),
                      to: isInfinity(dc.to) ? null : (dc.to ?? null),
                      value: dc.value ?? 1,
                      isInfinity: isInfinity(dc.to),
                      isFromInfinity: isFromInfinity(dc.from),
                    };
                    const isFromInfinityEffective = isFirst ? false : draft.isFromInfinity;
                    const fromValue = isFirst ? -100 : (draft.from ?? '');
                    const hasFromError = dampingValidationErrors[id]?.from;
                    const hasToError = dampingValidationErrors[id]?.to;
                    const hasValueError = dampingValidationErrors[id]?.value;
                    const hasConflictError = dampingConflictErrors[id];

                    return (
                      <Form key={dc.id} className={styles.dampingItem}>
                      <Form.Field inline className={styles.dampingField}>
                        <label>от (%)</label>
                        <div className={styles.dampingToWrapper}>
                          <div className={styles.dampingToInputWrapper}>
                            {isFromInfinityEffective ? (
                              <div className={styles.infinityIconWrapper}>
                                <InfinityIcon />
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="any"
                                min="-100"
                                disabled={isFirst}
                                className={classNames({ [styles.fieldError]: hasFromError })}
                                value={fromValue}
                                onChange={(e) => {
                                  if (isFirst) {
                                    return;
                                  }
                                  let value = e.target.value;
                                  if (value !== '') {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue < -100) {
                                      value = '-100';
                                    }
                                  }
                                  handleDampingFieldChange(
                                    id,
                                    'from',
                                    value === '' ? null : value,
                                  );
                                }}
                                placeholder="Не указано"
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            size="mini"
                            toggle
                            disabled={isFirst}
                            active={isFromInfinityEffective}
                            className={styles.infinityButton}
                            onClick={() => {
                              if (isFirst) {
                                return;
                              }
                              handleDampingFromInfinityToggle(id, !draft.isFromInfinity);
                            }}
                          >
                            Бесконечное значение
                          </Button>
                        </div>
                      </Form.Field>
                      <Form.Field inline className={styles.dampingField}>
                        <label>до (%)</label>
                        <div className={styles.dampingToWrapper}>
                          <div className={styles.dampingToInputWrapper}>
                            {draft.isInfinity ? (
                              <div className={styles.infinityIconWrapper}>
                                <InfinityIcon />
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="any"
                                min="-100"
                                className={classNames({ [styles.fieldError]: hasToError })}
                                ref={(node) => {
                                  dampingToInputRefs.current[id] = node;
                                }}
                                value={draft.to ?? ''}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  if (value !== '') {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue < -100) {
                                      value = '-100';
                                    }
                                  }
                                  handleDampingFieldChange(
                                    id,
                                    'to',
                                    value === '' ? null : value,
                                  );
                                }}
                                placeholder="Не указано"
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            size="mini"
                            toggle
                            active={draft.isInfinity}
                            className={styles.infinityButton}
                            onClick={() => handleDampingInfinityToggle(id, !draft.isInfinity)}
                          >
                            Бесконечное значение
                          </Button>
                        </div>
                      </Form.Field>
                      <Form.Field inline className={styles.dampingField}>
                        <label>Коэффициент</label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          className={classNames({ [styles.fieldError]: hasValueError })}
                          value={draft.value ?? 1}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Заменяем запятую на точку
                            value = value.replace(',', '.');
                            if (value !== '') {
                              const numValue = Number(value);
                              if (!isNaN(numValue) && numValue < 0) {
                                value = '0';
                              }
                            }
                            handleDampingFieldChange(id, 'value', value);
                          }}
                        />
                      </Form.Field>
                      <Button
                        type="button"
                        icon="trash"
                        negative
                        className={styles.dampingDeleteButton}
                        onClick={() => handleDampingCoefficientDelete(id)}
                      />
                      {hasConflictError && draft.isInfinity && (
                        <div className={styles.conflictError}>
                          Конфликт с другими значениями
                        </div>
                      )}
                    </Form>
                  );
                })}
                {/* Новые элементы */}
                {newDampingCoefficients.map((newItem, index) => {
                  const id = String(newItem.tempId);
                  const isFirst = visibleDampingCoefficients.length === 0 && index === 0;
                  const draft = dampingDraftsById[id] || {
                    from: isFirst ? -100 : null,
                    to: null,
                    value: 1,
                    isInfinity: false,
                    isFromInfinity: false,
                  };
                  const isFromInfinityEffective = isFirst ? false : draft.isFromInfinity;
                  const fromValue = isFirst ? -100 : (draft.from ?? '');
                  const hasFromError = dampingValidationErrors[id]?.from;
                  const hasToError = dampingValidationErrors[id]?.to;
                  const hasValueError = dampingValidationErrors[id]?.value;
                  const hasConflictError = dampingConflictErrors[id];

                  return (
                    <Form key={newItem.tempId} className={styles.dampingItem}>
                      <Form.Field inline className={styles.dampingField}>
                        <label>от (%)</label>
                        <div className={styles.dampingToWrapper}>
                          <div className={styles.dampingToInputWrapper}>
                            {isFromInfinityEffective ? (
                              <div className={styles.infinityIconWrapper}>
                                <InfinityIcon />
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="any"
                                min="-100"
                                disabled={isFirst}
                                className={classNames({ [styles.fieldError]: hasFromError })}
                                value={fromValue}
                                onChange={(e) => {
                                  if (isFirst) {
                                    return;
                                  }
                                  let value = e.target.value;
                                  if (value !== '') {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue < -100) {
                                      value = '-100';
                                    }
                                  }
                                  handleDampingFieldChange(
                                    id,
                                    'from',
                                    value === '' ? null : value,
                                  );
                                }}
                                placeholder="Не указано"
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            size="mini"
                            toggle
                            disabled={isFirst}
                            active={isFromInfinityEffective}
                            className={styles.infinityButton}
                            onClick={() => {
                              if (isFirst) {
                                return;
                              }
                              handleDampingFromInfinityToggle(id, !draft.isFromInfinity);
                            }}
                          >
                            Бесконечное значение
                          </Button>
                        </div>
                      </Form.Field>
                      <Form.Field inline className={styles.dampingField}>
                        <label>до (%)</label>
                        <div className={styles.dampingToWrapper}>
                          <div className={styles.dampingToInputWrapper}>
                            {draft.isInfinity ? (
                              <div className={styles.infinityIconWrapper}>
                                <InfinityIcon />
                              </div>
                            ) : (
                              <input
                                type="number"
                                step="any"
                                min="-100"
                                className={classNames({ [styles.fieldError]: hasToError })}
                                ref={(node) => {
                                  dampingToInputRefs.current[id] = node;
                                }}
                                value={draft.to ?? ''}
                                onChange={(e) => {
                                  let value = e.target.value;
                                  if (value !== '') {
                                    const numValue = Number(value);
                                    if (!isNaN(numValue) && numValue < -100) {
                                      value = '-100';
                                    }
                                  }
                                  handleDampingFieldChange(
                                    id,
                                    'to',
                                    value === '' ? null : value,
                                  );
                                }}
                                placeholder="Не указано"
                              />
                            )}
                          </div>
                          <Button
                            type="button"
                            size="mini"
                            toggle
                            active={draft.isInfinity}
                            className={styles.infinityButton}
                            onClick={() => handleDampingInfinityToggle(id, !draft.isInfinity)}
                          >
                            Бесконечное значение
                          </Button>
                        </div>
                      </Form.Field>
                      <Form.Field inline className={styles.dampingField}>
                        <label>Коэффициент</label>
                        <input
                          type="number"
                          step="any"
                          min="0"
                          className={classNames({ [styles.fieldError]: hasValueError })}
                          value={draft.value ?? 1}
                          onChange={(e) => {
                            let value = e.target.value;
                            // Заменяем запятую на точку
                            value = value.replace(',', '.');
                            if (value !== '') {
                              const numValue = Number(value);
                              if (!isNaN(numValue) && numValue < 0) {
                                value = '0';
                              }
                            }
                            handleDampingFieldChange(newItem.tempId, 'value', value);
                          }}
                        />
                      </Form.Field>
                      <Button
                        type="button"
                        icon="trash"
                        negative
                        className={styles.dampingDeleteButton}
                        onClick={() => handleDampingCoefficientDelete(newItem.tempId)}
                      />
                      {hasConflictError && draft.isInfinity && (
                        <div className={styles.conflictError}>
                          Конфликт с другими значениями
                        </div>
                      )}
                    </Form>
                  );
                })}
              </div>
            )}
            <div className={styles.dampingBlock}>
              <Button type="button" positive content="Добавить коэффициент" onClick={handleDampingCoefficientCreate} />
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitleRow}>
              <div className={styles.sectionTitle}>
                ТИП КОНТЕНТА (БАЛЛЫ ЗА ЗАДАЧУ ПО УМОЛЧАНИЮ)
              </div>
              <SectionInfo
                title="ТИП КОНТЕНТА (БАЛЛЫ ЗА ЗАДАЧУ ПО УМОЛЧАНИЮ)"
                content="Справочник типов контента и базовых баллов. При выборе типа в карточке эти баллы используются как базовые (до применения демпфирующего коэффициента и дополнительных баллов)."
              />
              {canEditPointSystemSettings && hasHiddenContentTypes && (
                <Checkbox
                  toggle
                  className={styles.showHiddenToggle}
                  label="Показать скрытые типы"
                  checked={showHiddenContentTypes}
                  onChange={(_, { checked }) => setShowHiddenContentTypes(Boolean(checked))}
                />
              )}
            </div>
          </div>
          <div className={styles.sectionBody}>
            <Tab
              className={classNames(styles.contentCardDefaultsTabs, {
                [styles.readOnlyNavigation]: !canEditPointSystemSettings,
              })}
              activeIndex={contentTypeTabIndex}
              onTabChange={handleContentTypeTabChange}
              menu={{
                secondary: true,
                pointing: true,
                className: styles.contentTypeTabMenu,
              }}
              panes={contentCardDefaultsPanes}
            />
          </div>
        </div>
      </div>
      </div>
                )}
              </div>
            </div>
    </Modal.Content>
    {canEditPointSystemSettings && selectedTemplateId && pointSystem && (
    <Modal.Actions className={styles.modalActions}>
      {saveMessage && (
        <div className={classNames(styles.saveMessage, { [styles.error]: saveMessage.type === 'error' })}>
          {saveMessage.text}
        </div>
      )}
      <div className={styles.saveButtonWrapper}>
        {currentBoardName && (
          <Button
            type="button"
            primary
            size="small"
            onClick={handleApplyToBoardRequest}
            disabled={!selectedTemplateId}
          >
            {`Применить к доске ${currentBoardName}`}
          </Button>
        )}
        <Button positive onClick={handleSave} size="small" disabled={!hasUnsavedChanges()}>
          Сохранить
        </Button>
      </div>
    </Modal.Actions>
    )}
  </Modal>
  <Modal
    open={showDampingGapsConfirmation}
    size="small"
    centered={false}
    closeOnDimmerClick={false}
    onClose={() => {
      pendingDampingGapsFixedDraftsRef.current = null;
      setShowDampingGapsConfirmation(false);
    }}
  >
    <Modal.Header>Подтверждение</Modal.Header>
    <Modal.Content>
      <div style={{ marginBottom: '20px' }}>
        В 'Настройка демпфирующего коэффициента' обнаружены 'зазоры между значениями'. Хотите автоматически
        скорректировать значения?
      </div>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={handleDampingGapsSaveAsIs}>
        Сохранить как есть
      </Button>
      <Button positive onClick={handleDampingGapsFixAndSave}>
        Скорректировать и сохранить
      </Button>
    </Modal.Actions>
  </Modal>
  <Modal
    open={showCloseConfirmation}
    size="small"
    centered={false}
    closeOnDimmerClick={false}
    onClose={() => setShowCloseConfirmation(false)}
  >
    <Modal.Header>Подтверждение</Modal.Header>
    <Modal.Content>
      <div style={{ marginBottom: '20px' }}>
        У вас есть несохраненные изменения. Вы уверены, что хотите закрыть окно без сохранения?
      </div>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={() => setShowCloseConfirmation(false)}>
        Отмена
      </Button>
      <Button negative onClick={handleCloseConfirm}>
        Закрыть без сохранения
      </Button>
    </Modal.Actions>
  </Modal>
  <Modal
    open={showApplyToBoardConfirmation}
    size="small"
    centered={false}
    closeOnDimmerClick={false}
    onClose={handleApplyToBoardCancel}
  >
    <Modal.Header>Подтверждение</Modal.Header>
    <Modal.Content>
      <div style={{ marginBottom: '20px' }}>{applyConfirmContent}</div>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={handleApplyToBoardCancel}>Отмена</Button>
      <Button positive onClick={handleApplyToBoardConfirmAndSave}>
        Применить
      </Button>
    </Modal.Actions>
  </Modal>
  <Modal
    open={Boolean(templateIdToDelete)}
    size="small"
    centered={false}
    closeOnDimmerClick={false}
    onClose={handleTemplateDeleteCancel}
  >
    <Modal.Header>Скрыть шаблон?</Modal.Header>
    <Modal.Content>
      <div style={{ marginBottom: '20px' }}>{templateDeleteConfirmContent}</div>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={handleTemplateDeleteCancel}>Отмена</Button>
      <Button negative onClick={handleTemplateDeleteConfirm}>
        Скрыть
      </Button>
    </Modal.Actions>
  </Modal>
  <Modal
    open={Boolean(templateIdToRestore)}
    size="small"
    centered={false}
    closeOnDimmerClick={false}
    onClose={handleTemplateRestoreCancel}
  >
    <Modal.Header>Восстановить шаблон?</Modal.Header>
    <Modal.Content>
      <div style={{ marginBottom: '20px' }}>{templateRestoreConfirmContent}</div>
    </Modal.Content>
    <Modal.Actions>
      <Button onClick={handleTemplateRestoreCancel}>Отмена</Button>
      <Button positive onClick={handleTemplateRestoreConfirm}>
        Восстановить
      </Button>
    </Modal.Actions>
  </Modal>
  <Modal
    open={contentTypeFormOpen}
    size="mini"
    centered={false}
    closeOnDimmerClick={false}
    onClose={closeContentTypeForm}
  >
    <Modal.Header>
      {contentTypeFormMode === 'edit' ? 'Редактировать тип контента' : 'Добавить тип контента'}
    </Modal.Header>
    <Modal.Content>
      <Form onSubmit={handleContentTypeFormSubmit}>
        <Form.Input
          label="Название"
          value={contentTypeFormTitle}
          disabled={contentTypeFormIsHidden}
          onChange={(e) => setContentTypeFormTitle(e.target.value)}
          placeholder="Введите название"
          autoFocus
        />
        <Form.Field>
          <label>Иконка</label>
          <div className={styles.contentTypeIconDropdownWrap}>
            {!!contentTypeFormIcon && (
              <Icon name={contentTypeFormIcon} className={styles.contentTypeIconDropdownPreview} />
            )}
            <Dropdown
              fluid
              selection
              search={searchContentTypeIconOptions}
              clearable
              disabled={contentTypeFormIsHidden}
              selectOnBlur={false}
              selectOnNavigation={false}
              placeholder="Выберите иконку"
              options={contentTypeIconOptions}
              value={contentTypeFormIcon || null}
              text={selectedContentTypeIconTitle || undefined}
              onChange={(e, { value }) => setContentTypeFormIcon(value || '')}
              className={styles.contentTypeIconDropdown}
            />
          </div>
        </Form.Field>
      </Form>
    </Modal.Content>
    <Modal.Actions>
      {contentTypeFormMode === 'edit' &&
        (contentTypeFormIsHidden ? (
          <RestorePopup
            title="Восстановить тип контента"
            content="Тип снова станет доступен для выбора и редактирования."
            buttonContent="Восстановить"
            onConfirm={handleContentTypeFormRestore}
          >
            <Button type="button" positive floated="left" icon="undo" content="Восстановить" />
          </RestorePopup>
        ) : (
          <DeletePopup
            title="Скрыть тип контента"
            content="Тип будет скрыт и недоступен для выбора. Исторические расчёты и связанные данные не изменятся."
            buttonContent="Скрыть"
            onConfirm={handleContentTypeFormDelete}
          >
            <Button type="button" negative floated="left" icon="trash" content="Скрыть" />
          </DeletePopup>
        ))}
      <Button type="button" onClick={closeContentTypeForm}>
        Отмена
      </Button>
      <Button
        type="button"
        positive
        content={contentTypeFormMode === 'edit' ? 'Сохранить' : 'Добавить'}
        onClick={handleContentTypeFormSubmit}
        disabled={
          contentTypeFormIsHidden || !String(contentTypeFormTitle || '').trim()
        }
      />
    </Modal.Actions>
  </Modal>
      </>
    );
  },
);

PointSystemModal.propTypes = {
  open: PropTypes.bool,
  /* eslint-disable react/forbid-prop-types */
  pointSystem: PropTypes.object,
  dampingCoefficients: PropTypes.array,
  employeePlans: PropTypes.array,
  contentTypes: PropTypes.array,
  contentTypeTemplates: PropTypes.array,
  contentViewPlans: PropTypes.array,
  selectedTemplateId: PropTypes.string,
  boardSourceTemplateId: PropTypes.string,
  currentBoardName: PropTypes.string,
  /* eslint-enable react/forbid-prop-types */
  onEmployeePlansFetch: PropTypes.func.isRequired,
  onEmployeePlanCreate: PropTypes.func.isRequired,
  onEmployeePlanUpdate: PropTypes.func.isRequired,
  onEmployeePlanDelete: PropTypes.func.isRequired,
  onContentViewPlansFetch: PropTypes.func.isRequired,
  onContentViewPlanGetOrCreate: PropTypes.func.isRequired,
  onContentViewPlanUpdate: PropTypes.func.isRequired,
  onContentCardDefaultsFetch: PropTypes.func.isRequired,
  onContentCardDefaultCreate: PropTypes.func.isRequired,
  onContentCardDefaultUpdate: PropTypes.func.isRequired,
  onContentCardDefaultDelete: PropTypes.func.isRequired,
  onDampingCoefficientsFetch: PropTypes.func.isRequired,
  onDampingCoefficientCreate: PropTypes.func.isRequired,
  onDampingCoefficientUpdate: PropTypes.func.isRequired,
  onDampingCoefficientDelete: PropTypes.func.isRequired,
  onContentTypesFetch: PropTypes.func.isRequired,
  onContentTypeCreate: PropTypes.func.isRequired,
  onContentTypeUpdate: PropTypes.func.isRequired,
  onContentTypeDelete: PropTypes.func.isRequired,
  onContentTypeTemplatesFetch: PropTypes.func.isRequired,
  onContentTypeTemplateCreate: PropTypes.func.isRequired,
  onContentTypeTemplateUpdate: PropTypes.func.isRequired,
  onContentTypeTemplateDelete: PropTypes.func.isRequired,
  onContentTypeTemplateItemCreate: PropTypes.func.isRequired,
  onContentTypeTemplateItemUpdate: PropTypes.func.isRequired,
  onContentTypeTemplateItemDelete: PropTypes.func.isRequired,
  onContentTypeTemplateApply: PropTypes.func.isRequired,
  onPointSystemUpdate: PropTypes.func.isRequired,
  onSelectTemplate: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  canEditPointSystemSettings: PropTypes.bool,
};

PointSystemModal.defaultProps = {
  open: true,
  pointSystem: null,
  dampingCoefficients: [],
  employeePlans: [],
  contentTypes: [],
  contentTypeTemplates: [],
  contentViewPlans: [],
  selectedTemplateId: null,
  boardSourceTemplateId: null,
  currentBoardName: '',
  onSelectTemplate: undefined,
  canEditPointSystemSettings: true,
};

export default PointSystemModal;

